import { create } from 'zustand';
import type { WorkflowDefinition, Action, Trigger, Position, RunAfter, AgentTool, AgentAction, IfAction, ForeachAction, SwitchAction, UntilAction } from '@/types/workflowDefinition';
import { isAgentAction, isIfAction, isForeachAction, isSwitchAction, isUntilAction } from '@/types/workflowDefinition';
import type { Note } from '@/types/note';
import { DEFAULT_NOTE_SIZE, DEFAULT_NOTE_COLOR } from '@/types/note';
import type { ConnectionsConfiguration } from '@/types/logicApps';
import type { MockNodeInfo, TriggerMockPayload } from '@/types/templateMode';
import { useConnectionStore } from './connectionStore';
import { extractReferenceKeyFromOperation } from '@/services/connectionService';
import { useNotificationStore } from './notificationStore';
import {
  validateActionName,
  validateTriggerName,
  validateToolName,
  validateToolInnerActionName,
  validateForEachConstraints,
  FOREACH_NESTED_MESSAGE,
  FOREACH_NESTED_TITLE,
  FOREACH_INVALID_STRUCTURE_TITLE,
  type NameValidationResult,
} from '@/utils/validation';
import type { ScopeContext } from './nodeEditorStore';
import type {
  NodeDefinitionTarget,
  ReplaceNodeDefinitionResult,
} from '@/types/nodeCodeEditing';
import {
  getBranchTypeFromHandle,
  removeRunAfterReferencesRecursive,
  reformRunAfterOnDeleteRecursive,
  actionContainsRecursive,
  insertActionIntoBranch,
  findActionInBranch,
} from './branchActionHelpers';
import { getActionsThatRunAfterSource, getActionsToAddInScope } from '@/components/WorkflowDesigner/helpers/node';
import {
  getLayoutMode,
  findActionLocations,
  layoutAfterDelete,
  layoutAfterInsert,
  resolveNodeDefinitionLocation,
  type ActionTreeLocation,
  walkAllActions,
  updateActionReferences,
} from '@/utils/workflow';
import {
  injectActionPositions,
  injectTriggerPositions,
  extractActionPositions,
  extractTriggerPositions,
  extractToolNodePositions,
  stripActionPositions,
  stripTriggerPositions,
} from '@/utils/workflow';
import { useWorkflowViewPrefsStore, type WorkflowIdentity } from './workflowViewPrefsStore';
import { actionNodeId, useWorkflowLayoutStore, type WorkflowMode } from './workflowLayoutStore';

/** Warn the user when an action would create a nested ForEach, which is not allowed. */
function notifyNestedForEachBlocked(): void {
  useNotificationStore.getState().addNotification({
    type: 'warning',
    title: FOREACH_NESTED_TITLE,
    message: FOREACH_NESTED_MESSAGE,
  });
}

/**
 * Result of finding an action in the workflow hierarchy
 */
type ActionLocation = {
  action: Action;
  /** The actions record containing this action */
  container: Record<string, Action>;
  /** Path description for debugging */
  path: string;
};

/**
 * Collect all action names recursively, including nested scopes (If/Foreach/Switch/Until).
 * Used for generating globally-unique action names across the entire workflow tree.
 */
const collectAllActionNames = (actions: Record<string, Action>): Set<string> => {
  const names = new Set<string>();
  for (const [name, action] of Object.entries(actions)) {
    names.add(name);
    if (isIfAction(action)) {
      const ifAction = action as IfAction;
      if (ifAction.actions) collectAllActionNames(ifAction.actions).forEach(n => names.add(n));
      if (ifAction.else?.actions) collectAllActionNames(ifAction.else.actions).forEach(n => names.add(n));
    }
    if (isForeachAction(action)) {
      const foreachAction = action as ForeachAction;
      if (foreachAction.actions) collectAllActionNames(foreachAction.actions).forEach(n => names.add(n));
    }
    if (isUntilAction(action)) {
      const untilAction = action as UntilAction;
      if (untilAction.actions) collectAllActionNames(untilAction.actions).forEach(n => names.add(n));
    }
    if (isSwitchAction(action)) {
      const switchAction = action as SwitchAction;
      if (switchAction.cases) {
        for (const caseData of Object.values(switchAction.cases)) {
          const typedCase = caseData as { case: string; actions?: Record<string, Action> };
          if (typedCase.actions) collectAllActionNames(typedCase.actions).forEach(n => names.add(n));
        }
      }
      if (switchAction.default?.actions) collectAllActionNames(switchAction.default.actions).forEach(n => names.add(n));
    }
  }
  return names;
};

/**
 * Find an action by name, searching recursively through nested structures (If/Foreach/Switch/Until)
 */
const findActionRecursive = (
  actions: Record<string, Action>,
  name: string,
  path: string = 'actions'
): ActionLocation | null => {
  // Check top-level first
  if (actions[name]) {
    return { action: actions[name], container: actions, path };
  }

  // Search in nested structures
  for (const [actionName, action] of Object.entries(actions)) {
    if (isIfAction(action)) {
      const ifAction = action as IfAction;
      // Check true branch
      if (ifAction.actions) {
        const found = findActionRecursive(ifAction.actions, name, `${path}.${actionName}.actions`);
        if (found) return found;
      }
      // Check false branch
      if (ifAction.else?.actions) {
        const found = findActionRecursive(ifAction.else.actions, name, `${path}.${actionName}.else.actions`);
        if (found) return found;
      }
    }

    if (isForeachAction(action)) {
      const foreachAction = action as ForeachAction;
      if (foreachAction.actions) {
        const found = findActionRecursive(foreachAction.actions, name, `${path}.${actionName}.actions`);
        if (found) return found;
      }
    }

    if (isUntilAction(action)) {
      const untilAction = action as UntilAction;
      if (untilAction.actions) {
        const found = findActionRecursive(untilAction.actions, name, `${path}.${actionName}.actions`);
        if (found) return found;
      }
    }

    if (isSwitchAction(action)) {
      const switchAction = action as SwitchAction;
      // Check each case
      if (switchAction.cases) {
        for (const [caseName, caseData] of Object.entries(switchAction.cases)) {
          const typedCase = caseData as { case: string; actions?: Record<string, Action> };
          if (typedCase.actions) {
            const found = findActionRecursive(typedCase.actions, name, `${path}.${actionName}.cases.${caseName}.actions`);
            if (found) return found;
          }
        }
      }
      // Check default case
      if (switchAction.default?.actions) {
        const found = findActionRecursive(switchAction.default.actions, name, `${path}.${actionName}.default.actions`);
        if (found) return found;
      }
    }
  }

  return null;
};

type AgentActionLocation = Omit<ActionTreeLocation<Action>, 'action'> & {
  action: AgentAction;
};

type AgentActionResolution =
  | { ok: true; actionName: string; location: AgentActionLocation }
  | { ok: false; error: string };

type AgentActionUpdatePreparation =
  | { ok: true; actionName: string; location: AgentActionLocation; workflow: WorkflowDefinition }
  | { ok: false; error: string };

const resolveAgentActionLocation = (
  actions: Record<string, Action>,
  agentName: string,
): AgentActionResolution => {
  const actionName = agentName.includes('/') ? agentName.split('/').pop()! : agentName;
  const locations = findActionLocations(actions, actionName);

  if (locations.length === 0) {
    return { ok: false, error: `Agent "${agentName}" was not found in the workflow` };
  }

  if (locations.length > 1) {
    return {
      ok: false,
      error: `Agent "${agentName}" appears more than once in the workflow. Rename the duplicate actions before managing tools.`,
    };
  }

  const location = locations[0]!;
  if (!isAgentAction(location.action)) {
    return { ok: false, error: `Action "${agentName}" is not an Agent action` };
  }

  return {
    ok: true,
    actionName,
    location: {
      ...location,
      action: location.action as AgentAction,
    },
  };
};

const prepareAgentActionUpdate = (
  workflow: WorkflowDefinition,
  agentName: string,
): AgentActionUpdatePreparation => {
  let clonedWorkflow: WorkflowDefinition;
  try {
    clonedWorkflow = JSON.parse(JSON.stringify(workflow)) as WorkflowDefinition;
  } catch {
    return { ok: false, error: `Agent "${agentName}" could not be updated because the workflow could not be cloned` };
  }

  const resolution = resolveAgentActionLocation(clonedWorkflow.definition.actions, agentName);
  if (!resolution.ok) {
    return resolution;
  }

  return {
    ok: true,
    actionName: resolution.actionName,
    location: resolution.location,
    workflow: clonedWorkflow,
  };
};

const notifyAgentToolError = (message: string): void => {
  useNotificationStore.getState().addNotification({
    type: 'error',
    title: 'Unable to manage agent tools',
    message,
  });
};

/**
 * Cache for findScopeContext to avoid repeated tree walks on every node click.
 * Invalidated when the workflow definition changes.
 */
const scopeContextCache = new Map<string, ScopeContext | undefined>();
let scopeContextCacheKey: string | undefined;

/** Invalidate the scope context cache (call when workflow definition changes) */
export const invalidateScopeContextCache = () => {
  scopeContextCache.clear();
  scopeContextCacheKey = undefined;
};

/**
 * Find the scope context for an action (e.g., detect if it's inside a ForEach loop).
 * Returns ScopeContext if the action is inside a ForEach, undefined otherwise.
 */
export const findScopeContext = (
  actionName: string,
  actions: Record<string, Action>,
  parentScopes: Array<{ actionName: string; type: string }> = []
): ScopeContext | undefined => {
  // Check if target exists at this level
  if (Object.hasOwn(actions, actionName)) {
    const foreachScopes = parentScopes.filter((s) => s.type === 'Foreach');
    const innermost = foreachScopes[foreachScopes.length - 1];
    if (innermost) {
      return {
        parentActionName: innermost.actionName,
        branchType: 'loop',
        parentScopes: foreachScopes,
      };
    }
    return undefined;
  }

  // Recurse into child containers
  for (const [name, action] of Object.entries(actions)) {
    if (isForeachAction(action)) {
      const foreachAction = action as ForeachAction;
      if (foreachAction.actions) {
        const newScopes = [...parentScopes, { actionName: name, type: 'Foreach' }];
        const nested = findScopeContext(actionName, foreachAction.actions, newScopes);
        if (nested) return nested;
      }
    } else if (isIfAction(action)) {
      const ifAction = action as IfAction;
      if (ifAction.actions) {
        const nested = findScopeContext(actionName, ifAction.actions, parentScopes);
        if (nested) return nested;
      }
      if (ifAction.else?.actions) {
        const nested = findScopeContext(actionName, ifAction.else.actions, parentScopes);
        if (nested) return nested;
      }
    } else if (isSwitchAction(action)) {
      const switchAction = action as SwitchAction;
      if (switchAction.cases) {
        for (const caseData of Object.values(switchAction.cases)) {
          const typedCase = caseData as { case: string; actions?: Record<string, Action> };
          if (typedCase.actions) {
            const nested = findScopeContext(actionName, typedCase.actions, parentScopes);
            if (nested) return nested;
          }
        }
      }
      if (switchAction.default?.actions) {
        const nested = findScopeContext(actionName, switchAction.default.actions, parentScopes);
        if (nested) return nested;
      }
    }
  }
  return undefined;
};

/**
 * Delete an action from a nested structure, returning the updated actions
 */
const deleteActionRecursive = (
  actions: Record<string, Action>,
  name: string
): Record<string, Action> => {
  // If the action is at this level, remove it
  if (actions[name]) {
    const newActions = { ...actions };
    delete newActions[name];
    return newActions;
  }

  // Otherwise, recursively update nested structures
  const newActions: Record<string, Action> = {};

  for (const [actionName, action] of Object.entries(actions)) {
    let updatedAction = action;

    if (isIfAction(action)) {
      const ifAction = action as IfAction;
      let modified = false;
      let newIfAction = { ...ifAction };

      if (ifAction.actions) {
        const newNestedActions = deleteActionRecursive(ifAction.actions, name);
        if (newNestedActions !== ifAction.actions) {
          newIfAction = { ...newIfAction, actions: newNestedActions };
          modified = true;
        }
      }
      if (ifAction.else?.actions) {
        const newElseActions = deleteActionRecursive(ifAction.else.actions, name);
        if (newElseActions !== ifAction.else.actions) {
          newIfAction = { ...newIfAction, else: { ...ifAction.else, actions: newElseActions } };
          modified = true;
        }
      }

      if (modified) {
        updatedAction = newIfAction as Action;
      }
    }

    if (isForeachAction(action)) {
      const foreachAction = action as ForeachAction;
      if (foreachAction.actions) {
        const newNestedActions = deleteActionRecursive(foreachAction.actions, name);
        if (newNestedActions !== foreachAction.actions) {
          updatedAction = { ...foreachAction, actions: newNestedActions } as Action;
        }
      }
    }

    if (isUntilAction(action)) {
      const untilAction = action as UntilAction;
      if (untilAction.actions) {
        const newNestedActions = deleteActionRecursive(untilAction.actions, name);
        if (newNestedActions !== untilAction.actions) {
          updatedAction = { ...untilAction, actions: newNestedActions } as Action;
        }
      }
    }

    if (isSwitchAction(action)) {
      const switchAction = action as SwitchAction;
      let modified = false;
      let newSwitchAction = { ...switchAction };

      if (switchAction.cases) {
        const newCases: typeof switchAction.cases = {};
        for (const [caseName, caseData] of Object.entries(switchAction.cases)) {
          const typedCase = caseData as { case: string; actions?: Record<string, Action> };
          if (typedCase.actions) {
            const newCaseActions = deleteActionRecursive(typedCase.actions, name);
            if (newCaseActions !== typedCase.actions) {
              newCases[caseName] = { ...typedCase, actions: newCaseActions };
              modified = true;
            } else {
              newCases[caseName] = caseData;
            }
          } else {
            newCases[caseName] = caseData;
          }
        }
        if (modified) {
          newSwitchAction = { ...newSwitchAction, cases: newCases };
        }
      }

      if (switchAction.default?.actions) {
        const newDefaultActions = deleteActionRecursive(switchAction.default.actions, name);
        if (newDefaultActions !== switchAction.default.actions) {
          newSwitchAction = { ...newSwitchAction, default: { ...switchAction.default, actions: newDefaultActions } };
          modified = true;
        }
      }

      if (modified) {
        updatedAction = newSwitchAction as Action;
      }
    }

    newActions[actionName] = updatedAction;
  }

  return newActions;
};

/**
 * Replace runAfter references from oldName to newName recursively.
 */
const replaceRunAfterReferencesRec = (
  actions: Record<string, Action>,
  oldName: string,
  newName: string
): Record<string, Action> => {
  const entries = Object.entries(actions);
  const mapped = entries.map(
    ([name, action]) => [name, replaceRunAfterReference(action, oldName, newName)] as const,
  );
  return mapped.some(([, a], i) => a !== entries[i]![1])
    ? Object.fromEntries(mapped)
    : actions;
};

/**
 * Process a single action: replace runAfter references and recurse into child scopes.
 */
const replaceRunAfterReference = (
  action: Action,
  oldName: string,
  newName: string,
): Action => {
  const updatedAction = action.runAfter && Object.hasOwn(action.runAfter, oldName)
    ? (() => {
        const { [oldName]: oldStatuses, ...rest } = action.runAfter!;
        return { ...action, runAfter: { ...rest, ...(oldStatuses ? { [newName]: oldStatuses } : {}) } };
      })()
    : action;

  if ((isForeachAction(updatedAction) || isUntilAction(updatedAction)) && updatedAction.actions) {
    const updatedNestedActions = replaceRunAfterReferencesRec(updatedAction.actions, oldName, newName);
    if (updatedNestedActions !== updatedAction.actions) return { ...updatedAction, actions: updatedNestedActions } as Action;
    return updatedAction;
  }

  if (isIfAction(updatedAction)) {
    let updatedActionRec = updatedAction as IfAction;
    if (updatedActionRec.actions) {
      const updatedNestedActions = replaceRunAfterReferencesRec(updatedActionRec.actions, oldName, newName);
      if (updatedNestedActions !== updatedActionRec.actions) updatedActionRec = { ...updatedActionRec, actions: updatedNestedActions };
    }
    if (updatedActionRec.else?.actions) {
      const updatedNestedActions = replaceRunAfterReferencesRec(updatedActionRec.else.actions, oldName, newName);
      if (updatedNestedActions !== updatedActionRec.else.actions) updatedActionRec = { ...updatedActionRec, else: { ...updatedActionRec.else, actions: updatedNestedActions } };
    }
    return updatedActionRec !== updatedAction ? updatedActionRec as Action : updatedAction;
  }

  if (isSwitchAction(updatedAction)) {
    let updatedActionRec = updatedAction as SwitchAction;
    if (updatedActionRec.cases) {
      const updatedCases = Object.fromEntries(
        Object.entries(updatedActionRec.cases).map(([caseName, caseData]) => {
          const typedCase = caseData as { case: string; actions?: Record<string, Action> };
          if (!typedCase.actions) return [caseName, caseData];
          const updatedNestedActions = replaceRunAfterReferencesRec(typedCase.actions, oldName, newName);
          return updatedNestedActions !== typedCase.actions
            ? [caseName, { ...typedCase, actions: updatedNestedActions }]
            : [caseName, caseData];
        }),
      );
      const casesChanged = Object.keys(updatedCases).some(
        (k) => updatedCases[k] !== updatedActionRec.cases![k],
      );
      if (casesChanged) updatedActionRec = { ...updatedActionRec, cases: updatedCases };
    }
    if (updatedActionRec.default?.actions) {
      const updatedNestedActions = replaceRunAfterReferencesRec(updatedActionRec.default.actions, oldName, newName);
      if (updatedNestedActions !== updatedActionRec.default.actions) {
        updatedActionRec = { ...updatedActionRec, default: { ...updatedActionRec.default, actions: updatedNestedActions } };
      }
    }
    return updatedActionRec !== updatedAction ? updatedActionRec as Action : updatedAction;
  }

  if (isAgentAction(updatedAction) && (updatedAction as AgentAction).tools) {
    const tools = (updatedAction as AgentAction).tools!;
    const updatedTools = Object.fromEntries(
      Object.entries(tools).map(([toolName, tool]) => {
        if (!tool.actions) return [toolName, tool];
        const updatedNestedActions = replaceRunAfterReferencesRec(tool.actions, oldName, newName);
        return updatedNestedActions !== tool.actions
          ? [toolName, { ...tool, actions: updatedNestedActions }]
          : [toolName, tool];
      }),
    );
    const toolsChanged = Object.keys(updatedTools).some((k) => updatedTools[k] !== tools[k]);
    if (toolsChanged) return { ...(updatedAction as AgentAction), tools: updatedTools } as Action;
  }

  return updatedAction;
};


/**
 * Add an action to a nested structure at the same level as a sibling action
 */
const addActionAtSameLevel = (
  actions: Record<string, Action>,
  siblingName: string,
  newName: string,
  newAction: Action
): Record<string, Action> => {
  // If the sibling is at this level, add the new action here
  if (actions[siblingName]) {
    return { ...actions, [newName]: newAction };
  }

  // Otherwise, recursively search and add
  const newActions: Record<string, Action> = {};

  for (const [actionName, action] of Object.entries(actions)) {
    let updatedAction = action;

    if (isIfAction(action)) {
      const ifAction = action as IfAction;
      let modified = false;
      let newIfAction = { ...ifAction };

      if (ifAction.actions?.[siblingName]) {
        newIfAction = { ...newIfAction, actions: { ...ifAction.actions, [newName]: newAction } };
        modified = true;
      } else if (ifAction.actions) {
        const result = addActionAtSameLevel(ifAction.actions, siblingName, newName, newAction);
        if (result !== ifAction.actions) {
          newIfAction = { ...newIfAction, actions: result };
          modified = true;
        }
      }

      if (!modified && ifAction.else?.actions?.[siblingName]) {
        newIfAction = { ...newIfAction, else: { ...ifAction.else, actions: { ...ifAction.else.actions, [newName]: newAction } } };
        modified = true;
      } else if (!modified && ifAction.else?.actions) {
        const result = addActionAtSameLevel(ifAction.else.actions, siblingName, newName, newAction);
        if (result !== ifAction.else.actions) {
          newIfAction = { ...newIfAction, else: { ...ifAction.else, actions: result } };
          modified = true;
        }
      }

      if (modified) {
        updatedAction = newIfAction as Action;
      }
    }

    if (isForeachAction(action)) {
      if (action.actions) {
        const result = addActionAtSameLevel(action.actions, siblingName, newName, newAction);
        if (result !== action.actions) {
          updatedAction = { ...action, actions: result } as Action;
        }
      }
    }

    if (isSwitchAction(action)) {
      const switchAction = action as SwitchAction;
      let modified = false;
      let newSwitchAction = { ...switchAction };

      if (switchAction.cases) {
        for (const [caseName, caseData] of Object.entries(switchAction.cases)) {
          const typedCase = caseData as { case: string; actions?: Record<string, Action> };
          if (typedCase.actions?.[siblingName]) {
            const newCases = { ...switchAction.cases };
            newCases[caseName] = { ...typedCase, actions: { ...typedCase.actions, [newName]: newAction } };
            newSwitchAction = { ...newSwitchAction, cases: newCases };
            modified = true;
            break;
          } else if (typedCase.actions) {
            const result = addActionAtSameLevel(typedCase.actions, siblingName, newName, newAction);
            if (result !== typedCase.actions) {
              const newCases = { ...switchAction.cases };
              newCases[caseName] = { ...typedCase, actions: result };
              newSwitchAction = { ...newSwitchAction, cases: newCases };
              modified = true;
              break;
            }
          }
        }
      }

      if (!modified && switchAction.default?.actions?.[siblingName]) {
        newSwitchAction = { ...newSwitchAction, default: { ...switchAction.default, actions: { ...switchAction.default.actions, [newName]: newAction } } };
        modified = true;
      } else if (!modified && switchAction.default?.actions) {
        const result = addActionAtSameLevel(switchAction.default.actions, siblingName, newName, newAction);
        if (result !== switchAction.default.actions) {
          newSwitchAction = { ...newSwitchAction, default: { ...switchAction.default, actions: result } };
          modified = true;
        }
      }

      if (modified) {
        updatedAction = newSwitchAction as Action;
      }
    }

    newActions[actionName] = updatedAction;
  }

  return newActions;
};

/*
 * Normalize a workflow definition to fix common issues from LLM-generated workflows.
 * This cleans up malformed Agent actions and other structural issues.
 */
function normalizeWorkflow(workflow: WorkflowDefinition): WorkflowDefinition {
  const normalized = { ...workflow };

  if (normalized.definition) {
    normalized.definition = { ...normalized.definition };

    // Normalize triggers first so we know the trigger names for runAfter sanitization
    if (normalized.definition.triggers) {
      normalized.definition.triggers = normalizeTriggers(normalized.definition.triggers);
    }

    const triggerNames = new Set(Object.keys(normalized.definition.triggers ?? {}));

    // Normalize actions
    if (normalized.definition.actions) {
      normalized.definition.actions = normalizeActions(normalized.definition.actions, triggerNames);
    }
  }

  return normalized;
}

/**
 * Strip any runAfter entries that point at trigger names. Triggers are not
 * valid runAfter dependencies in Logic Apps — an action that "runs after" a
 * trigger is expressed with an empty `runAfter`. Carrying a trigger key here
 * produces a runtime validation error and is the canonical source of bug #955.
 */
function sanitizeRunAfter(runAfter: RunAfter | undefined, triggerNames: Set<string>): RunAfter | undefined {
  if (!runAfter) {
    return runAfter;
  }
  let dirty = false;
  const cleaned: RunAfter = {};
  for (const [dep, statuses] of Object.entries(runAfter) as Array<[string, string[]]>) {
    if (triggerNames.has(dep)) {
      dirty = true;
      continue;
    }
    cleaned[dep] = statuses;
  }
  return dirty ? cleaned : runAfter;
}

/**
 * Normalize a collection of actions
 */
function normalizeActions(actions: Record<string, Action>, triggerNames: Set<string>): Record<string, Action> {
  const normalized: Record<string, Action> = {};

  for (const [name, action] of Object.entries(actions)) {
    normalized[name] = normalizeAction(action, triggerNames);
  }

  return normalized;
}

/**
 * Normalize triggers to fix inputs nesting issues
 */
function normalizeTriggers(triggers: Record<string, Trigger>): Record<string, Trigger> {
  const normalized: Record<string, Trigger> = {};

  for (const [name, trigger] of Object.entries(triggers)) {
    normalized[name] = normalizeTrigger(trigger);
  }

  return normalized;
}

/**
 * Normalize a single trigger
 */
function normalizeTrigger(trigger: Trigger): Trigger {
  const normalized = { ...trigger };

  if (normalized.inputs && typeof normalized.inputs === 'object' && !Array.isArray(normalized.inputs)) {
    normalized.inputs = normalizeInputs(normalized.inputs as Record<string, unknown>);
  }

  return normalized;
}

/**
 * Normalize a single action, handling Agent-specific cleanup,
 * stripping invalid trigger references from runAfter, and recursing
 * into scope children (If/Foreach/Until/Switch/Agent tools).
 */
function normalizeAction(action: Action, triggerNames: Set<string>): Action {
  const base = isAgentAction(action) ? normalizeAgentAction(action) : { ...action };

  if (!isAgentAction(action) && base.inputs && typeof base.inputs === 'object' && !Array.isArray(base.inputs)) {
    base.inputs = normalizeInputs(base.inputs as Record<string, unknown>);
  }

  // `metadata.detached` is the persisted form of `action.detached` (stripped on
  // save — see workflowSerializer.syncDetachedToMetadata). Lift it back so the
  // graph doesn't auto-reconnect a previously detached node to the trigger.
  if (base.metadata?.detached === true && base.detached !== true) {
    base.detached = true;
  }

  // Sanitize runAfter — drop any keys that point at trigger names.
  const sanitizedRunAfter = sanitizeRunAfter(base.runAfter, triggerNames);
  if (sanitizedRunAfter !== base.runAfter) {
    base.runAfter = sanitizedRunAfter;
  }

  // Recurse into scope children so nested actions are also sanitized.
  if (isIfAction(action)) {
    const ia = base as IfAction;
    if (ia.actions) {
      (base as IfAction).actions = normalizeActions(ia.actions, triggerNames);
    }
    if (ia.else?.actions) {
      (base as IfAction).else = { ...ia.else, actions: normalizeActions(ia.else.actions, triggerNames) };
    }
  } else if (isSwitchAction(action)) {
    const sa = base as SwitchAction;
    if (sa.cases) {
      const newCases: SwitchAction['cases'] = {};
      for (const [caseName, caseDef] of Object.entries(sa.cases)) {
        const cd = caseDef as { case: string | number; actions?: Record<string, Action> };
        newCases[caseName] = (cd.actions
          ? { ...cd, actions: normalizeActions(cd.actions, triggerNames) }
          : cd) as SwitchAction['cases'][string];
      }
      (base as SwitchAction).cases = newCases;
    }
    if (sa.default?.actions) {
      (base as SwitchAction).default = { ...sa.default, actions: normalizeActions(sa.default.actions, triggerNames) };
    }
  } else if (isForeachAction(action) || isUntilAction(action)) {
    const loop = base as Action & { actions?: Record<string, Action> };
    if (loop.actions) {
      (base as typeof loop).actions = normalizeActions(loop.actions, triggerNames);
    }
  } else if (isAgentAction(action)) {
    const ag = base as Action & { tools?: Record<string, AgentTool> };
    if (ag.tools) {
      const newTools: Record<string, AgentTool> = {};
      for (const [toolName, tool] of Object.entries(ag.tools) as Array<[string, AgentTool]>) {
        newTools[toolName] = tool.actions
          ? { ...tool, actions: normalizeActions(tool.actions, triggerNames) }
          : tool;
      }
      (base as Action & { tools?: Record<string, AgentTool> }).tools = newTools;
    }
  }

  return base;
}

/**
 * Normalize inputs object by removing the nested 'inputs' key
 * which is a common bug from our save logic or LLM generation
 */
function normalizeInputs(inputs: Record<string, unknown>): Record<string, unknown> {
  const normalized = { ...inputs };

  // Remove nested 'inputs' key - this is the JSON blob that shouldn't exist
  if ('inputs' in normalized && typeof normalized.inputs === 'string') {
    delete normalized.inputs;
  }

  // Strip `retryPolicy: { type: 'default' }` — the runtime treats the default
  // policy as implicit, the editor's serializer never emits it, so carrying it
  // in the in-memory workflow would only create a diff between what the editor
  // writes and what the validate endpoint receives.
  const rp = normalized.retryPolicy;
  if (
    rp &&
    typeof rp === 'object' &&
    !Array.isArray(rp) &&
    (rp as Record<string, unknown>).type === 'default' &&
    Object.keys(rp as Record<string, unknown>).length === 1
  ) {
    delete normalized.retryPolicy;
  }

  return normalized;
}

/**
 * Normalize an Agent action to fix common structural issues:
 * - Remove inputs.inputs (nested stringified blob)
 * - Move inputs.tools to top-level tools
 * - Remove invalid keys like inputs.model, inputs.systemPrompt
 * - Remove invalid keys from inputs.parameters (model, systemPrompt, tools)
 */
function normalizeAgentAction(action: Action): Action {
  const normalized = { ...action } as Action & { tools?: Record<string, AgentTool> };

  if (!normalized.inputs || typeof normalized.inputs !== 'object') {
    return normalized;
  }

  const inputs = { ...normalized.inputs } as Record<string, unknown>;

  // Remove nested 'inputs' key (JSON blob that shouldn't exist)
  if ('inputs' in inputs && typeof inputs.inputs === 'string') {
    delete inputs.inputs;
  }

  // Handle tools - if it's inside inputs, move to top level
  if ('tools' in inputs) {
    const inputTools = inputs.tools;

    // If tools is a string (e.g., "{}"), try to parse it
    if (typeof inputTools === 'string') {
      try {
        const parsedTools = JSON.parse(inputTools);
        if (typeof parsedTools === 'object' && parsedTools !== null) {
          // Merge with existing tools at top level
          normalized.tools = { ...(normalized.tools || {}), ...parsedTools };
        }
      } catch {
        // Invalid JSON, ignore
      }
    } else if (typeof inputTools === 'object' && inputTools !== null) {
      // It's already an object, merge with top level
      normalized.tools = { ...(normalized.tools || {}), ...(inputTools as Record<string, AgentTool>) };
    }

    // Remove from inputs
    delete inputs.tools;
  }

  // Remove keys that shouldn't be directly in inputs (they should only be in inputs.parameters)
  const invalidInputKeys = ['model', 'systemPrompt'];
  for (const key of invalidInputKeys) {
    if (key in inputs) {
      delete inputs[key];
    }
  }

  // Clean up inputs.parameters - remove invalid keys that LLM sometimes generates
  if (inputs.parameters && typeof inputs.parameters === 'object') {
    const params = { ...inputs.parameters } as Record<string, unknown>;

    // Keys that should NOT be in parameters (LLM hallucinations)
    // tools should be at top level (sibling to inputs), not inside inputs.parameters
    const invalidParamKeys = ['model', 'systemPrompt', 'tools'];
    for (const key of invalidParamKeys) {
      if (key in params) {
        delete params[key];
      }
    }

    inputs.parameters = params;
  }

  normalized.inputs = inputs;

  return normalized;
}

type ActiveFile = 'workflow.json' | 'connections.json';
export type LayoutOrientation = 'horizontal' | 'vertical';

/**
 * Information about where to insert a new action in the workflow
 */
export type InsertionPoint = {
  sourceNodeId: string; // Node after which to insert (trigger-xxx or action-xxx)
  targetNodeId?: string; // Node before which to insert (for inserting between nodes)
  scope?: {
    parentActionName: string; // Name of the control flow action (e.g., Foreach name)
    branchType: string; // 'loop' for foreach body, 'true'/'false' for if, 'case-X'/'default' for switch
  };
};

/**
 * Represents a selected node in the workflow
 */
export type SelectedNode = {
  id: string; // Node ID (e.g., 'trigger-manual' or 'action-HTTP')
  name: string; // Node name (e.g., 'manual' or 'HTTP')
  type: 'trigger' | 'action' | 'tool';
  // For tools, the parent agent name
  parentAgentName?: string;
  // For tools, the tool name within the agent
  toolName?: string;
  // For tools, the name of the inner action within the tool
  innerActionName?: string;
  // Connection name for retrieving connector info (including brand color)
  connectionName?: string;
  // Scope context when action is inside a ForEach or other scope container
  scopeContext?: ScopeContext;
};

type WorkflowState = {
  workflow: WorkflowDefinition | null;
  connections: ConnectionsConfiguration | null;
  // Code editor state
  activeFile: ActiveFile;
  workflowJson: string;
  connectionsJson: string;
  // Canvas settings
  layoutOrientation: LayoutOrientation;
  /**
   * Composite identity of the workflow currently loaded in the designer.
   * Set by `WorkflowDesignerPage` after `setWorkflowFiles`. When non-null,
   * `setLayoutOrientation` writes through to the persistent
   * `workflowViewPrefsStore` so per-workflow orientation survives reloads.
   */
  currentWorkflowIdentity: WorkflowIdentity | null;
  /**
   * Which branch of the layout store (draft vs published) the editor is
   * currently bound to. Position writes always target this mode; reads in
   * components that subscribe via `useWorkflowLayoutStore` should pass this
   * value so they paint the right canvas for the version being viewed.
   * Mirrored from `useDraftModeStore.isDraftMode` by `WorkflowDesignerPage`.
   */
  currentMode: WorkflowMode;
  // Selection state - supports single or multiple selection
  selectedNode: SelectedNode | null;
  // Multi-selection state - for Ctrl/Cmd+click or box selection
  selectedNodes: SelectedNode[];
  // Layout state
  isLayouting: boolean;
  // Dirty state - tracks unsaved changes since last history save
  isDirty: boolean;
  // Change count - incremented on every change for autosave tracking
  changeCount: number;
  /**
   * Whether the Code panel's editor buffer diverges from what's currently
   * committed to the store. Set by `CodeEditor.handleChange` even when the
   * buffer fails to parse — otherwise a user editing `workflow.json` who
   * transiently produces invalid JSON (empty file, unclosed brace) sees Save
   * silently disabled with no explanation of why. Cleared on workflow load and
   * when the editor buffer is re-seeded from the committed tree (file switch /
   * successful parse). Intentionally not cleared by `markClean` to avoid
   * racing in-flight keystrokes during save.
   */
  codeBufferDirty: boolean;
  /**
   * Parse error from the Code panel buffer's most recent debounced tick, or
   * `null` if the buffer parsed cleanly. Surfaced by `ManualSaveButton` as a
   * tooltip and used to refuse manual save without silently swallowing the
   * user's intent. Autosave intentionally ignores this — the workflow tree
   * still holds the last-known-good state and should keep persisting.
   */
  codeBufferParseError: string | null;
  // ── Mock data (sidecar; NOT part of the serialized workflow definition) ──
  /**
   * Per-node mock state keyed by canvas node id (`trigger-<name>` /
   * `action-<scoped>`). Seeded from the applied template's mocks and editable
   * via the Mock Data tab. Kept deliberately OUT of `workflow` /
   * `workflowJson` so it never leaks into workflow.json. Client-side
   * persistence is deferred — this slice is in-memory for now.
   */
  mockedNodes: Record<string, MockNodeInfo | TriggerMockPayload>;
  /**
   * Global "run with mocks" switch driven by the Test dialog (Ask 6). When
   * true a test run submits mock overrides; when false it runs the draft live.
   */
  mockRunEnabled: boolean | undefined;
};

type WorkflowActions = {
  setWorkflowFiles: (
    workflow: WorkflowDefinition | null,
    connections: ConnectionsConfiguration | null,
  ) => void;
  setActiveFile: (file: ActiveFile) => void;
  updateWorkflow: (workflow: WorkflowDefinition) => void;
  updateWorkflowFromJson: (json: string) => { success: boolean; error?: string };
  updateConnectionsFromJson: (json: string) => { success: boolean; error?: string };
  replaceNodeDefinition: (
    target: NodeDefinitionTarget,
    definition: Action | Trigger,
  ) => ReplaceNodeDefinitionResult;
  getAction: (name: string) => Action | undefined;
  getTrigger: (name: string) => Trigger | undefined;
  getTool: (agentName: string, toolName: string) => AgentTool | undefined;
  updateAction: (name: string, action: Action) => void;
  updateTrigger: (name: string, trigger: Trigger) => void;
  updateActionPosition: (name: string, position: Position) => void;
  updateTriggerPosition: (name: string, position: Position) => void;
  updateNodePositions: (
    positions: ReadonlyMap<string, Position> | Record<string, Position>,
  ) => void;
  updateToolNodePosition: (agentName: string, toolName: string, position: Position) => void;
  wrapActionInImplicitForeach: (
    actionName: string,
    foreachExpression: string,
    loopBaseName?: string
  ) => { loopName: string; created: boolean } | null;
  addEdge: (source: { source: string; handle: string | null; }, target: { target: string; handle: string | null; }) => void;
  removeEdge: (sourceId: string, targetId: string, sourceHandle?: string | null) => void;
  setLayoutOrientation: (orientation: LayoutOrientation) => void;
  clearWorkflow: () => void;
  // New actions for adding/deleting nodes
  addTrigger: (name: string, trigger: Trigger) => void;
  addAction: (name: string, action: Action, insertionPoint?: InsertionPoint) => void;
  duplicateAction: (name: string) => string | null;
  pasteActionFromClipboard: (action: Action, originalName: string) => string | null;
  deleteAction: (name: string) => void;
  deleteTrigger: (name: string) => void;
  addToolToAgent: (agentName: string, toolName: string, tool: AgentTool) => void;
  deleteToolFromAgent: (agentName: string, toolName: string) => void;
  updateTool: (agentName: string, toolName: string, tool: Partial<AgentTool>) => void;
  setToolMcpAllowedTools: (agentName: string, toolName: string, allowedTools: string[] | undefined) => void;
  /**
   * Write the connection reference key onto a managed MCP tool's
   * inputs.connectionReference.connectionName. Preserves the existing
   * connectionReference.api block (which carries the connector id).
   */
  setToolMcpConnectionName: (agentName: string, toolName: string, connectionName: string) => void;
  updateToolInnerAction: (agentName: string, toolName: string, innerActionName: string, action: Action) => void;
  generateUniqueActionName: (baseName: string) => string;
  generateUniqueToolName: (agentName: string, baseName: string) => string;
  initializeEmptyWorkflow: () => void;
  // Rename actions
  renameAction: (oldName: string, newName: string) => NameValidationResult;
  renameTrigger: (oldName: string, newName: string) => NameValidationResult;
  renameTool: (agentName: string, oldToolName: string, newToolName: string) => NameValidationResult;
  renameToolInnerAction: (agentName: string, toolName: string, oldActionName: string, newActionName: string) => NameValidationResult;
  // Switch case value
  updateSwitchCaseValue: (switchName: string, caseName: string, newValue: string) => void;
  // Selection actions (single-select for backward compatibility)
  selectNode: (node: SelectedNode | null) => void;
  selectNodeByName: (name: string, type: 'trigger' | 'action', connectionName?: string) => void;
  selectTool: (agentName: string, toolName: string) => void;
  openNodeEditor: (node: SelectedNode | null) => void; // Opens editor without changing multi-selection
  closeNodeEditor: () => void; // Closes editor without changing multi-selection
  /** Highlight a single node (keyboard nav) WITHOUT opening the node editor. */
  focusNode: (node: SelectedNode | null) => void;
  clearSelection: () => void;
  // Multi-selection actions
  selectNodes: (nodes: SelectedNode[]) => void;
  addNodesToSelection: (nodes: SelectedNode[]) => void;
  addToSelection: (node: SelectedNode) => void;
  removeFromSelection: (nodeId: string) => void;
  toggleNodeSelection: (node: SelectedNode) => void;
  isNodeSelected: (nodeId: string) => boolean;
  // Layout actions
  setIsLayouting: (isLayouting: boolean) => void;
  applyLayoutPositions: (positions: Map<string, Position>, skipDirty?: boolean) => void;
  /**
   * Lift legacy embedded positions (`metadata.position` /
   * `metadata.toolNodePositions`) out of the current workflow definition
   * into the layout store, then strip them from the in-memory workflow.
   * Idempotent — calling this when the workflow is already clean is a no-op.
   * Used by `WorkflowDesignerPage` to cover the case where the workflow
   * loads before the identity has been resolved.
   */
  migrateLegacyPositions: (identity: WorkflowIdentity) => void;
  // Dirty state actions
  markDirty: () => void;
  markClean: () => void;
  /**
   * Update the Code panel buffer's dirty + parse-error state. Called from
   * `CodeEditor.handleChange` after each debounced tick. Passing
   * `{ dirty: false, parseError: null }` clears both — e.g. when the buffer
   * is re-seeded from the tree on file switch or on load.
   */
  setCodeBufferState: (input: { dirty: boolean; parseError: string | null }) => void;
  // Change count action
  resetChangeCount: () => void;
  // Note actions
  addNote: (position: Position) => string;
  updateNote: (id: string, note: Partial<Note>) => void;
  deleteNote: (id: string) => void;
  // ── Mock data actions ──
  // User-facing edits (setMockedNode/removeMockedNode/setMockNodeEnabled/
  // setMockRunEnabled) mark the workflow dirty so the draft save picks up the
  // mockData.json sidecar. They never write mock state into `workflowJson`.
  /** Add or replace the mock entry for a node. Marks the workflow dirty. */
  setMockedNode: (nodeId: string, info: MockNodeInfo | TriggerMockPayload) => void;
  /** Remove a node's mock entry entirely. Marks the workflow dirty. */
  removeMockedNode: (nodeId: string) => void;
  /** Replace the entire mock map (used by template injection). Non-dirtying. */
  setMockedNodes: (mocks: Record<string, MockNodeInfo | TriggerMockPayload>) => void;
  /** Toggle the per-action mock on/off (no-op for trigger entries). Marks dirty. */
  setMockNodeEnabled: (nodeId: string, enabled: boolean) => void;
  /** Set the global "run with mocks" switch. Marks the workflow dirty. */
  setMockRunEnabled: (enabled: boolean) => void;
  /**
   * Seed the mock slice from a persisted sidecar (load / mode-switch reapply)
   * WITHOUT marking the workflow dirty — avoids a save loop on hydration.
   */
  hydrateMocks: (
    mockedNodes: Record<string, MockNodeInfo | TriggerMockPayload>,
    mockRunEnabled: boolean | undefined,
  ) => void;
  /** Drop all mock state (called when the workflow is cleared/replaced). */
  clearMocks: () => void;
};

type WorkflowStore = WorkflowState & WorkflowActions;

export const useWorkflowStore = create<WorkflowStore>()((set, get) => ({
  workflow: null,
  connections: null,
  activeFile: 'workflow.json',
  workflowJson: '',
  connectionsJson: '',
  layoutOrientation: 'horizontal',
  currentWorkflowIdentity: null,
  currentMode: 'draft',
  selectedNode: null,
  selectedNodes: [],
  isLayouting: false,
  isDirty: false,
  changeCount: 0,
  codeBufferDirty: false,
  codeBufferParseError: null,
  mockedNodes: {},
  mockRunEnabled: undefined,

  setWorkflowFiles: (workflow, connections) => {
    // Normalize the workflow to fix common issues from LLM generation or corrupted saves
    const normalized = workflow ? normalizeWorkflow(workflow) : null;

    // Migrate any legacy embedded positions (`metadata.position` /
    // `metadata.toolNodePositions`) into the client-side layout store, then
    // strip them from the in-memory workflow so the JSON view stays clean and
    // future saves can't leak layout back into the definition. The migration
    // is best-effort: it only runs when `currentWorkflowIdentity` is already
    // set. If identity isn't resolved yet, leave the embedded positions on
    // the workflow untouched — `WorkflowDesignerPage` calls
    // `migrateLegacyPositions` once the identity arrives, which performs the
    // same import-then-strip against the still-intact metadata. Stripping
    // here unconditionally would discard a legacy workflow's only copy of
    // its layout before anything had a chance to read it.
    let migratedDefinition = normalized;
    if (normalized) {
      const identity = get().currentWorkflowIdentity;
      if (identity) {
        const triggerPositions = extractTriggerPositions(normalized.definition.triggers ?? {});
        const actionPositions = extractActionPositions(normalized.definition.actions ?? {});
        const toolNodePositions = extractToolNodePositions(normalized.definition.actions ?? {});
        const combined: Record<string, Position> = { ...triggerPositions, ...actionPositions };
        if (Object.keys(combined).length > 0 || Object.keys(toolNodePositions).length > 0) {
          useWorkflowLayoutStore.getState().importLayout(identity, get().currentMode, {
            nodePositions: combined,
            toolNodePositions,
          });
        }
        migratedDefinition = {
          ...normalized,
          definition: {
            ...normalized.definition,
            triggers: stripTriggerPositions(normalized.definition.triggers ?? {}),
            actions: stripActionPositions(normalized.definition.actions ?? {}),
          },
        };
      }
    }

    set({
      workflow: migratedDefinition,
      connections,
      workflowJson: migratedDefinition ? JSON.stringify(migratedDefinition, null, 2) : '',
      connectionsJson: connections ? JSON.stringify(connections, null, 2) : '',
      // Reset dirty state when loading a workflow - we're starting from a clean state
      isDirty: false,
      // Any Code panel buffer divergence carried over from a previous workflow
      // is meaningless once we've loaded a fresh definition. Clear it so the
      // Save button doesn't inherit a phantom "unsaved changes" signal.
      codeBufferDirty: false,
      codeBufferParseError: null,
      // Loading a workflow replaces any in-memory mock state. Template apply
      // re-seeds these immediately afterward via computeTemplateMockedNodes at
      // the call site.
      mockedNodes: {},
      mockRunEnabled: undefined,
    });
  },

  setActiveFile: (file) => set({ activeFile: file }),

  updateWorkflow: (workflow) => {
    set({
      workflow,
      workflowJson: JSON.stringify(workflow, null, 2),
      isDirty: true, changeCount: get().changeCount + 1,
    });
  },

  updateWorkflowFromJson: (json) => {
    try {
      const parsed = JSON.parse(json);

      // Basic validation - ensure it has the expected structure
      if (!parsed.definition || typeof parsed.definition !== 'object') {
        return { success: false, error: 'Invalid workflow structure: missing definition' };
      }

      // Normalize the workflow to fix common issues from LLM generation
      const normalized = normalizeWorkflow(parsed);
      const normalizedJson = JSON.stringify(normalized, null, 2);

      // Validate ForEach constraints (single-action limit + no nesting)
      const { valid, errors: foreachErrors } = validateForEachConstraints(normalized as Record<string, unknown>);
      if (!valid) {
        useNotificationStore.getState().addNotification({
          type: 'error',
          title: FOREACH_INVALID_STRUCTURE_TITLE,
          message: foreachErrors.join('. '),
        });
        // Still apply to not lose user's work, but notify
      }

      set({
        workflow: normalized,
        workflowJson: normalizedJson,
        isDirty: true, changeCount: get().changeCount + 1,
      });

      // Sync changes to the node editor if it's open
      // Use dynamic import to avoid circular dependency
      import('@/stores/nodeEditorStore').then(({ useNodeEditorStore }) => {
        const nodeEditorState = useNodeEditorStore.getState();
        if (nodeEditorState.isOpen && nodeEditorState.node) {
          const { node } = nodeEditorState;
          // Find the updated node data in the new workflow
          let updatedNodeData: Action | Trigger | undefined;
          if (node.type === 'trigger') {
            updatedNodeData = normalized.definition.triggers[node.name];
          } else if (node.type === 'tool' && node.parentAgentName && node.innerActionName) {
            const agentResolution = resolveAgentActionLocation(
              normalized.definition.actions,
              node.parentAgentName,
            );
            if (agentResolution.ok) {
              const tool = agentResolution.location.action.tools?.[node.toolName || ''];
              updatedNodeData = tool?.actions?.[node.innerActionName] as Action | undefined;
            }
          } else {
            updatedNodeData = normalized.definition.actions[node.name];
          }

          if (updatedNodeData) {
            nodeEditorState.refreshFromWorkflow(updatedNodeData);
          }
        }
      });

      return { success: true };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Invalid JSON' };
    }
  },

  updateConnectionsFromJson: (json) => {
    try {
      const parsed = JSON.parse(json);

      // Basic validation - connections.json should be an object
      if (typeof parsed !== 'object' || parsed === null) {
        return { success: false, error: 'Invalid connections structure: must be an object' };
      }

      const connectionStore = useConnectionStore.getState();
      connectionStore.initializeFromWorkflow(get().workflow, parsed as ConnectionsConfiguration);
      connectionStore.markDirty();

      set({
        connections: parsed,
        connectionsJson: json,
      });

      return { success: true };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Invalid JSON' };
    }
  },

  replaceNodeDefinition: (target, definition) => {
    const { workflow } = get();
    if (!workflow) {
      return { success: false, error: 'No workflow is loaded.' };
    }

    const nextWorkflow = structuredClone(workflow);
    const location = resolveNodeDefinitionLocation(nextWorkflow, target);
    if (!location) {
      return {
        success: false,
        error: 'This node changed or was removed. Reload its latest definition before applying.',
      };
    }

    location.container[location.key] = definition;
    invalidateScopeContextCache();
    set({
      workflow: nextWorkflow,
      workflowJson: JSON.stringify(nextWorkflow, null, 2),
      isDirty: true,
      changeCount: get().changeCount + 1,
    });
    useConnectionStore.getState().refreshMappingFromWorkflow(nextWorkflow);
    return { success: true };
  },

  getAction: (name) => {
    const { workflow } = get();
    if (!workflow) return undefined;

    // Strip scope prefix if present (e.g., "Until/Increment_variable" → "Increment_variable")
    const actionName = name.includes('/') ? name.split('/').pop()! : name;
    const locations = findActionLocations(workflow.definition.actions, actionName);
    return locations.length === 1 ? locations[0]!.action : undefined;
  },

  getTrigger: (name) => {
    const { workflow } = get();
    return workflow?.definition.triggers[name];
  },

  getTool: (agentName, toolName) => {
    const { workflow } = get();
    if (!workflow) return undefined;

    const resolution = resolveAgentActionLocation(workflow.definition.actions, agentName);
    if (!resolution.ok) return undefined;

    return resolution.location.action.tools?.[toolName];
  },

  updateAction: (name, action) => {
    const { workflow } = get();
    if (!workflow) return;

    // Strip scope prefix if present (e.g., "Until/Increment_variable" → "Increment_variable")
    const actionName = name.includes('/') ? name.split('/').pop()! : name;

    // Deep clone to avoid mutating state directly
    let newWorkflow: WorkflowDefinition;
    try {
      newWorkflow = JSON.parse(JSON.stringify(workflow));
    } catch (e) {
      console.error('[updateAction] Failed to clone workflow:', e);
      return;
    }

    // Safety check: ensure the cloned workflow has the expected structure
    if (!newWorkflow.definition?.triggers || !newWorkflow.definition?.actions) {
      console.error('[updateAction] Cloned workflow has invalid structure:', newWorkflow);
      return;
    }

    // Use findActionRecursive to locate the action at its actual nesting level
    // (it could be inside a Scope, ForEach, If, Switch, etc.)
    const location = findActionRecursive(newWorkflow.definition.actions, actionName);
    if (location) {
      // Update in-place at the correct nesting level via the container reference
      location.container[actionName] = action;
    } else {
      // Action not found anywhere — add at root level (new action case)
      newWorkflow.definition.actions[actionName] = action;
    }

    set({
      workflow: newWorkflow,
      workflowJson: JSON.stringify(newWorkflow, null, 2),
      isDirty: true, changeCount: get().changeCount + 1,
    });
  },

  updateTrigger: (name, trigger) => {
    const { workflow } = get();
    if (!workflow) return;

    // Safety check: ensure workflow has the expected structure
    if (!workflow.definition?.triggers) {
      console.error('[updateTrigger] Workflow has invalid structure:', workflow);
      return;
    }

    const newWorkflow = {
      ...workflow,
      definition: {
        ...workflow.definition,
        triggers: {
          ...workflow.definition.triggers,
          [name]: trigger,
        },
      },
    };

    set({
      workflow: newWorkflow,
      workflowJson: JSON.stringify(newWorkflow, null, 2),
      isDirty: true, changeCount: get().changeCount + 1,
    });
  },

  updateActionPosition: (name, position) => {
    const identity = get().currentWorkflowIdentity;
    if (!identity) return;

    // `name` may arrive as a React Flow node id (scoped) like "If_Cond/Response"
    // or as a plain action name. The layout store keys mirror the React Flow ids,
    // so we forward the scoped form unchanged; plain names get the bare prefix.
    useWorkflowLayoutStore
      .getState()
      .setNodePosition(identity, get().currentMode, `action-${name}`, position);
    // Dragging only changes layout state, not workflow definition, but the
    // sidecar file is part of "saved state" so dirty the workflow so the
    // next autosave (or manual save) PUTs nodesMetadata.json. Coalesce into
    // a single set — drag fires frequently, so two updates per call would
    // double store notifications and downstream re-renders.
    set((state) => ({ isDirty: true, changeCount: state.changeCount + 1 }));
  },

  updateTriggerPosition: (name, position) => {
    const identity = get().currentWorkflowIdentity;
    if (!identity) return;
    useWorkflowLayoutStore
      .getState()
      .setNodePosition(identity, get().currentMode, `trigger-${name}`, position);
    set((state) => ({ isDirty: true, changeCount: state.changeCount + 1 }));
  },

  updateNodePositions: (positions) => {
    const identity = get().currentWorkflowIdentity;
    if (!identity) return;
    const mode = get().currentMode;
    const layoutStore = useWorkflowLayoutStore.getState();
    const current = layoutStore.getNodePositions(identity, mode);
    const entries = positions instanceof Map
      ? positions
      : new Map(Object.entries(positions));
    const changed = new Map<string, Position>();
    for (const [nodeId, position] of entries) {
      const existing = current[nodeId];
      if (existing?.x === position.x && existing?.y === position.y) continue;
      changed.set(nodeId, position);
    }
    if (changed.size === 0) return;

    layoutStore.setNodePositions(identity, mode, changed);
    set((state) => ({ isDirty: true, changeCount: state.changeCount + 1 }));
  },

  updateToolNodePosition: (agentName, toolName, position) => {
    const identity = get().currentWorkflowIdentity;
    if (!identity) return;
    // Strip scope prefix so agents nested in scopes share a single tool layout.
    const cleanAgentName = agentName.includes('/') ? agentName.split('/').pop()! : agentName;
    useWorkflowLayoutStore
      .getState()
      .setToolNodePosition(identity, get().currentMode, cleanAgentName, toolName, position);
    set((state) => ({ isDirty: true, changeCount: state.changeCount + 1 }));
  },

  wrapActionInImplicitForeach: (actionName, foreachExpression, loopBaseName = 'For_each') => {
    const { workflow } = get();
    if (!workflow) return null;

    const originalActionName = actionName.includes('/') ? actionName.split('/').pop()! : actionName;

    let newWorkflow: WorkflowDefinition;
    try {
      newWorkflow = JSON.parse(JSON.stringify(workflow));
    } catch (e) {
      console.error('[wrapActionInImplicitForeach] Failed to clone workflow:', e);
      return null;
    }
    const topActions = newWorkflow.definition.actions ?? {};
    const location = findActionRecursive(topActions, originalActionName);
    if (!location) {
      return null;
    }

    // If any ancestor scope is a ForEach that iterates over the SAME array
    // expression, return that loop's name instead of double-wrapping. We walk
    // ALL ancestor action names in the path (not just the immediate parent)
    // so this works for actions nested inside If/Switch/Until within a ForEach.
    //
    // Path format from findActionRecursive:
    //   "actions.Loop_1.actions.Check.actions"  → action names: Loop_1, Check
    //   "actions.Loop_1.actions.Sw.cases.C1.actions" → action names: Loop_1, Sw
    if (location.path !== 'actions') {
      const pathSegments = location.path.split('.');
      for (let i = 0; i < pathSegments.length - 1; i++) {
        const nextSegment = pathSegments[i + 1];
        if (pathSegments[i] === 'actions' && nextSegment) {
          const ancestorLocation = findActionRecursive(topActions, nextSegment);
          if (ancestorLocation && isForeachAction(ancestorLocation.action)) {
            if (ancestorLocation.action.foreach === foreachExpression) {
              return { loopName: nextSegment, created: false };
            }
            notifyNestedForEachBlocked();
            return null;
          }
        }
      }
    }

    const originalAction = location.action;
    const loopActionName = get().generateUniqueActionName(loopBaseName);

    // Move upstream dependencies to the wrapping loop.
    const loopAction: ForeachAction = {
      type: 'Foreach',
      foreach: foreachExpression,
      runAfter: { ...(originalAction.runAfter ?? {}) },
      actions: {
        [originalActionName]: {
          ...originalAction,
          runAfter: {},
        },
      },
    };

    delete location.container[originalActionName];
    location.container[loopActionName] = loopAction as Action;

    const rewiredActions = replaceRunAfterReferencesRec(topActions, originalActionName, loopActionName);

    newWorkflow.definition.actions = rewiredActions;

    set({
      workflow: newWorkflow,
      workflowJson: JSON.stringify(newWorkflow, null, 2),
      isDirty: true, changeCount: get().changeCount + 1,
    });

    // Migrate layout positions so the new ForEach inherits the original action's
    // position and the canvas doesn't jump.  Node IDs use scoped paths:
    //   top-level "Compose_1"          → "action-Compose_1"
    //   inside scope "If/Compose_1"    → "action-If/Compose_1"
    const identity = get().currentWorkflowIdentity;
    if (identity) {
      const mode = get().currentMode;
      const layoutStore = useWorkflowLayoutStore.getState();

      const scopePrefix = location.path === 'actions'
        ? ''
        : location.path.split('.').filter((_, i, arr) => i > 0 && arr[i - 1] === 'actions').join('/');

      const oldNodeId = actionNodeId(scopePrefix, originalActionName);
      const loopNodeId = actionNodeId(scopePrefix, loopActionName);

      const oldPosition = layoutStore.getNodePosition(identity, mode, oldNodeId);
      if (oldPosition) {
        layoutStore.setNodePosition(identity, mode, loopNodeId, oldPosition);
        layoutStore.removeNode(identity, mode, oldNodeId);
      }
    }

    return { loopName: loopActionName, created: true };
  },

  addTrigger: (name, trigger) => {
    const { workflow } = get();
    if (!workflow) return;

    // Auto-switch to Agent kind if adding an agent trigger (Request + kind: Agent)
    const isAgentTrigger = trigger.type === 'Request' && trigger.kind === 'Agent';
    const newKind = isAgentTrigger ? 'Agent' : (workflow.kind ?? 'Stateful');

    // Lift any incoming `metadata.position` into the layout store, then
    // strip it from the trigger so the workflow JSON stays position-free.
    // Callers like `createTriggerFromOperation` still set `metadata.position`
    // for legacy reasons; the store boundary normalizes it.
    const incomingPosition = (trigger.metadata as { position?: Position } | undefined)?.position;
    const triggerIdentity = get().currentWorkflowIdentity;
    if (incomingPosition && triggerIdentity) {
      useWorkflowLayoutStore
        .getState()
        .setNodePosition(triggerIdentity, get().currentMode, `trigger-${name}`, incomingPosition);
    }
    let triggerToStore: Trigger = trigger;
    if (incomingPosition) {
      const { position: _pos, ...restMeta } = trigger.metadata as { position?: Position } & Record<string, unknown>;
      void _pos;
      triggerToStore = {
        ...trigger,
        metadata: Object.keys(restMeta).length > 0 ? restMeta : undefined,
      };
    }

    const newWorkflow = {
      ...workflow,
      kind: newKind,
      definition: {
        ...workflow.definition,
        triggers: {
          ...workflow.definition.triggers,
          [name]: triggerToStore,
        },
      },
    };

    set({
      workflow: newWorkflow,
      workflowJson: JSON.stringify(newWorkflow, null, 2),
      isDirty: true, changeCount: get().changeCount + 1,
    });

    // Mirror any auto-matched connection reference baked into the trigger's
    // inputs (host.connection.referenceName / serviceProviderConfiguration /
    // modelConfigurations) into the connection store mapping so badges and
    // pickers light up without waiting for the next workflow reload.
    const triggerRefKey = extractReferenceKeyFromOperation(triggerToStore as unknown as Record<string, unknown>);
    if (triggerRefKey) {
      useConnectionStore.getState().setNodeConnection(`trigger-${name}`, triggerRefKey);
    }
  },

  addEdge: (sourceInfo, targetInfo) => {
    const { workflow } = get();
    if (!workflow) return;

    const { source: sourceId, handle: sourceHandle } = sourceInfo;
    const { target: targetId } = targetInfo;

    // Prevent self-loops (node connecting to itself)
    if (sourceId === targetId) {
      console.warn('[workflowStore] Prevented self-loop edge:', sourceId);
      return;
    }

    // Parse the node IDs to get action/trigger names
    // Format: "trigger-{name}" or "action-{name}"
    // Edge IDs from useWorkflowGraph use scoped IDs (e.g. "action-If_Condition/Response");
    // strip the scope path so we work with the local action key.
    const rawTarget = targetId.replace(/^(trigger-|action-)/, '');
    const rawSource = sourceId.replace(/^(trigger-|action-)/, '');
    const targetName = rawTarget.includes('/') ? rawTarget.split('/').pop()! : rawTarget;
    const sourceName = rawSource.includes('/') ? rawSource.split('/').pop()! : rawSource;

    // Prevent self-referential runAfter (action depending on itself)
    if (targetName === sourceName) {
      console.warn('[workflowStore] Prevented self-referential runAfter:', targetName);
      return;
    }

    // Only edges into actions are meaningful (triggers can't have parents)
    if (!targetId.startsWith('action-')) return;

    // ─── Branch handle: reparent target into source's branch ────────────────
    // Dragging from an If's true/false handle, a Foreach/Until loop handle, or
    // a Switch case-X/default handle should NEST the target action into that
    // branch — not just create a runAfter pointing at the scope container.
    //
    // For an If's terminal/runAfter handle ("merge"), getBranchTypeFromHandle
    // returns null and we fall through to the default runAfter behaviour
    // below — that's how the third "After" edge from an If becomes a
    // sibling-level dependency rather than a nested branch action.
    const branchType = getBranchTypeFromHandle(sourceHandle);
    if (branchType && sourceId.startsWith('action-')) {
      const sourceLocation = findActionRecursive(workflow.definition.actions, sourceName);
      const sourceAction = sourceLocation?.action;
      if (!sourceAction) return;

      // Validate the handle is appropriate for the source action's type
      const handleMatchesSource =
        ((branchType === 'true' || branchType === 'false') && isIfAction(sourceAction)) ||
        (branchType === 'loop' && (isForeachAction(sourceAction) || isUntilAction(sourceAction))) ||
        ((branchType === 'default' || branchType.startsWith('case-')) && isSwitchAction(sourceAction));

      if (handleMatchesSource) {
        // Prevent moving an ancestor of the source into the source's own branch (cycle).
        const targetLocation = findActionRecursive(workflow.definition.actions, targetName);
        if (!targetLocation) return;
        if (actionContainsRecursive(targetLocation.action, sourceName)) {
          console.warn(`[workflowStore] Blocked reparenting "${targetName}" into "${sourceName}" — would create a cycle.`);
          return;
        }

        // Prevent nesting a ForEach inside a ForEach (loop body restriction).
        if (
          branchType === 'loop' &&
          isForeachAction(sourceAction) &&
          isForeachAction(targetLocation.action)
        ) {
          console.warn(`[workflowStore] Cannot nest ForEach inside ForEach "${sourceName}"`);
          notifyNestedForEachBlocked();
          return;
        }

        // Determine the set of actions to move. For If branches, include the
        // entire downstream chain of single-predecessor followers. For Foreach
        // 'loop', only allow a single action (no followers). For Switch and
        // Until branches, move just the target.
        const targetAction = targetLocation.action;
        const orderedExtraNames: string[] = [];
        const extraActions: Record<string, Action> = {};

        if (branchType === 'true' || branchType === 'false') {
          // If branch: target must not have runAfter dependencies on actions
          // other than the source itself (a runAfter on the source is naturally
          // absorbed by the structural reparenting).
          const targetRunAfterDeps = Object.keys(targetAction.runAfter ?? {}).filter((dep) => dep !== sourceName);
          if (targetRunAfterDeps.length) {
            console.warn(`[workflowStore] Cannot connect ${sourceName} (${branchType}) to ${targetName} because it already has runAfter dependencies. Try deleting edges before continuing.`);
            return;
          }
          // Compute the chain of followers; null means a merge point with an
          // external dependency was detected — bail.
          const chain = getActionsToAddInScope(targetName, workflow.definition.actions);
          if (chain === null) {
            console.warn(`[workflowStore] Cannot connect ${sourceName} (${branchType}) to ${targetName} because it has a merge point in its chain and cannot be added to If branch.`);
            return;
          }
          for (const [name, action] of Object.entries(chain)) {
            orderedExtraNames.push(name);
            extraActions[name] = action;
          }
        } else if (branchType === 'loop' && isForeachAction(sourceAction)) {
          // Foreach loop: target must not have runAfter dependencies on actions
          // other than the source itself, and must not have any chain followers
          // (single-action only).
          const targetRunAfterDeps = Object.keys(targetAction.runAfter ?? {}).filter((dep) => dep !== sourceName);
          if (targetRunAfterDeps.length) {
            console.warn(`[workflowStore] Cannot connect ${sourceName} (loop) to ${targetName} because it already has runAfter dependencies. Try deleting edges before continuing.`);
            return;
          }
          const followers = getActionsThatRunAfterSource(targetName, workflow.definition.actions);
          if (Object.keys(followers).length > 0) {
            console.warn(`[workflowStore] Cannot connect ${sourceName} (loop) to ${targetName} because Foreach only supports a single action.`);
            return;
          }
        }
        // Switch (default / case-X) and Until: single-action move only.

        // Snapshot the target being moved.
        const movingAction: Action = {
          ...targetAction,
          runAfter: {}, // Becomes an entry of the new branch.
        };
        delete movingAction.detached; // It now has a structural parent.

        // Remove the target and any chain followers from their current locations
        // and strip runAfter references pointing at any of them.
        let working = deleteActionRecursive(workflow.definition.actions, targetName);
        working = removeRunAfterReferencesRecursive(working, targetName);
        for (const extraName of orderedExtraNames) {
          working = deleteActionRecursive(working, extraName);
          working = removeRunAfterReferencesRecursive(working, extraName);
        }

        // Insert the target into the source's chosen branch.
        let inserted = insertActionIntoBranch(working, sourceName, branchType, targetName, movingAction);
        if (inserted === working) {
          // Branch insertion failed (parent not found / mismatched type). Bail.
          console.warn(`[workflowStore] Could not insert "${targetName}" into "${sourceName}" branch "${branchType}".`);
          return;
        }

        // Insert any chain followers, preserving their runAfter relationships
        // (which are internal to the chain — references to any actions outside
        // the chain were stripped above).
        for (const extraName of orderedExtraNames) {
          const extra: Action = { ...extraActions[extraName] };
          delete extra.detached;
          inserted = insertActionIntoBranch(inserted, sourceName, branchType, extraName, extra);
        }

        const newWorkflow: WorkflowDefinition = {
          ...workflow,
          definition: {
            ...workflow.definition,
            actions: inserted,
          },
        };

        set({
          workflow: newWorkflow,
          workflowJson: JSON.stringify(newWorkflow, null, 2),
          isDirty: true, changeCount: get().changeCount + 1,
        });
        return;
      }
      // Handle didn't match the source action's type — fall through to runAfter.
    }

    // ─── Default: add a runAfter dependency between siblings ────────────────
    // Locate the target wherever it lives (top level or nested in a scope) so
    // we can wire runAfter edges between siblings inside If/Foreach/Switch/Until
    // branches — not just at the root of the workflow.
    let newWorkflow: WorkflowDefinition;
    try {
      newWorkflow = JSON.parse(JSON.stringify(workflow));
    } catch (e) {
      console.error('[workflowStore.addEdge] Failed to clone workflow:', e);
      return;
    }

    const targetLocation = findActionRecursive(newWorkflow.definition.actions, targetName);
    if (!targetLocation) return;

    // For action→action edges, source and target must be siblings (live in the
    // same container) for runAfter to be structurally valid. This single check
    // covers cross-scope wires for If/Foreach/Switch/Until — including the
    // ForEach restriction previously enforced separately.
    if (sourceId.startsWith('action-')) {
      const sourceLocation = findActionRecursive(newWorkflow.definition.actions, sourceName);
      if (!sourceLocation || sourceLocation.container !== targetLocation.container) {
        console.warn(`[workflowStore] Blocked cross-scope runAfter: ${sourceName} → ${targetName}`);
        return;
      }
    } else {
      // Trigger sources are only valid as runAfter dependencies for top-level
      // actions — triggers aren't visible from inside a scope.
      if (targetLocation.container !== newWorkflow.definition.actions) {
        console.warn(`[workflowStore] Blocked runAfter from trigger to nested action: ${sourceName} → ${targetName}`);
        return;
      }
    }

    // Add the source as a runAfter dependency on the target, in-place at the
    // correct nesting level via the container reference returned by
    // findActionRecursive.
    const existingTargetAction = targetLocation.action;
    const existingRunAfter = existingTargetAction.runAfter || {};
    const triggerNames = new Set(Object.keys(newWorkflow.definition.triggers ?? {}));
    const sanitizedRunAfter = sanitizeRunAfter(existingRunAfter, triggerNames) ?? {};
    const isTriggerSource = sourceId.startsWith('trigger-');
    const updatedTargetAction: Action = {
      ...existingTargetAction,
      runAfter: isTriggerSource
        ? sanitizedRunAfter
        : {
            ...sanitizedRunAfter,
            [sourceName]: ['Succeeded'], // Default to running after success
          },
    };
    delete updatedTargetAction.detached; // Remove detached property when connecting

    targetLocation.container[targetName] = updatedTargetAction;

    set({
      workflow: newWorkflow,
      workflowJson: JSON.stringify(newWorkflow, null, 2),
      isDirty: true, changeCount: get().changeCount + 1,
    });
  },

  removeEdge: (sourceId, targetId, sourceHandle) => {
    const { workflow } = get();
    if (!workflow) return;

    // Parse the node IDs to get action/trigger names.
    // Edge IDs from useWorkflowGraph use scoped IDs (e.g. "action-If_Condition/Response");
    // strip the scope path so we work with the local action key.
    const rawTarget = targetId.replace(/^(trigger-|action-|tool-)/, '');
    const rawSource = sourceId.replace(/^(trigger-|action-)/, '');
    const targetName = rawTarget.includes('/') ? rawTarget.split('/').pop()! : rawTarget;
    const sourceName = rawSource.includes('/') ? rawSource.split('/').pop()! : rawSource;

    // Only remove edges from actions (not from triggers)
    if (!targetId.startsWith('action-')) {
      return;
    }

    // ─── Branch handle: extract target out of source's branch to top level ──
    // Right-clicking the dotted edge from an If's true/false handle (or a
    // Foreach loop / Switch case branch) to its child action and choosing
    // "Delete" should move the child back out of the branch into the
    // top-level workflow as a detached node — not silently no-op.
    const branchType = getBranchTypeFromHandle(sourceHandle);
    if (branchType && sourceId.startsWith('action-')) {
      const located = findActionInBranch(workflow.definition.actions, sourceName, branchType, targetName);
      if (located) {
        // Pull out and place at top level as detached.
        const extracted: Action = { ...located.action, runAfter: {}, detached: true };

        // Remove from the branch first…
        let newActions = located.remove();
        // …then strip any runAfter references to it from former siblings inside the branch.
        newActions = removeRunAfterReferencesRecursive(newActions, targetName);
        // Place at top level.
        newActions = { ...newActions, [targetName]: extracted };

        const newWorkflow: WorkflowDefinition = {
          ...workflow,
          definition: {
            ...workflow.definition,
            actions: newActions,
          },
        };

        set({
          workflow: newWorkflow,
          workflowJson: JSON.stringify(newWorkflow, null, 2),
          isDirty: true, changeCount: get().changeCount + 1,
        });
        return;
      }
      // Fall through if branch lookup didn't find the target (shouldn't normally happen).
    }

    // ─── Default: remove the runAfter dependency from a top-level action ────
    const targetAction = workflow.definition.actions[targetName];
    if (!targetAction) return;

    // Remove the source from runAfter
    const existingRunAfter = targetAction.runAfter || {};
    const newRunAfter = { ...existingRunAfter };
    delete newRunAfter[sourceName];

    // If action now has no parents, mark it as detached
    // to prevent automatic reconnection to triggers
    // Use empty runAfter: {} to indicate "no dependencies" (not undefined!)
    // Absence of runAfter would mean "runs after trigger" in Logic Apps
    const hasNoParents = Object.keys(newRunAfter).length === 0;

    const updatedAction: Action = {
      ...targetAction,
      runAfter: hasNoParents ? {} : newRunAfter,
      ...(hasNoParents ? { detached: true } : {}),
    };

    const newWorkflow = {
      ...workflow,
      definition: {
        ...workflow.definition,
        actions: {
          ...workflow.definition.actions,
          [targetName]: updatedAction,
        },
      },
    };

    set({
      workflow: newWorkflow,
      workflowJson: JSON.stringify(newWorkflow, null, 2),
      isDirty: true, changeCount: get().changeCount + 1,
    });
  },
  setLayoutOrientation: (orientation) => {
    set({ layoutOrientation: orientation });
    const identity = get().currentWorkflowIdentity;
    if (identity) {
      useWorkflowViewPrefsStore.getState().setOrientation(identity, orientation);
    }
  },

  clearWorkflow: () => {
    set({
      workflow: null,
      connections: null,
      activeFile: 'workflow.json',
      workflowJson: '',
      connectionsJson: '',
      layoutOrientation: useWorkflowViewPrefsStore.getState().getDefault(),
      currentWorkflowIdentity: null,
      // Clear selection too — there can't be a selected node when no
      // workflow is loaded, and leaving stale `selectedNode` here would
      // let the NodeEditor sync effect re-open the editor for a node
      // that no longer exists in the (now-empty) workflow.
      selectedNode: null,
      selectedNodes: [],
      isDirty: false,
      changeCount: 0,
      codeBufferDirty: false,
      codeBufferParseError: null,
      mockedNodes: {},
      mockRunEnabled: undefined,
    });
  },

  /**
   * Initialize an empty workflow structure for creating new workflows
   */
  initializeEmptyWorkflow: () => {
    const emptyWorkflow: WorkflowDefinition = {
      definition: {
        $schema: 'https://schema.management.azure.com/providers/Microsoft.Logic/schemas/2016-06-01/workflowdefinition.json#',
        triggers: {},
        actions: {},
        contentVersion: '1.0.0.0',
      },
    };

    set({
      workflow: emptyWorkflow,
      workflowJson: JSON.stringify(emptyWorkflow, null, 2),
    });
  },

  /**
   * Rename an action in the workflow.
   * Updates all runAfter references in other actions.
   *
   * @param oldName - Current name of the action
   * @param newName - New name for the action
   * @returns Validation result with normalized name if successful
   */
  renameAction: (oldName: string, newName: string): NameValidationResult => {
    const { workflow, selectedNode } = get();
    if (!workflow) {
      return { isValid: false, error: 'No workflow loaded' };
    }

    // Strip scope prefix if present (e.g., "Until/Increment_variable" → "Increment_variable")
    const plainOldName = oldName.includes('/') ? oldName.split('/').pop()! : oldName;

    // Search recursively so actions inside ForEach/Until/If/Switch can be renamed
    const location = findActionRecursive(workflow.definition.actions, plainOldName);
    if (!location) {
      return { isValid: false, error: `Action "${plainOldName}" not found` };
    }

    // Validate the new name
    const validation = validateActionName(newName, workflow, plainOldName);
    if (!validation.isValid || !validation.normalizedName) {
      return validation;
    }

    const normalizedName = validation.normalizedName;

    // If name hasn't changed, nothing to do
    if (normalizedName === plainOldName) {
      return { isValid: true, normalizedName };
    }

    // Rename the action within its container (which may be a nested scope)
    const renameInContainer = (container: Record<string, Action>): Record<string, Action> => {
      const newContainer: Record<string, Action> = {};
      for (const [actionName, actionDef] of Object.entries(container)) {
        if (actionName === plainOldName) {
          // Add with new name
          newContainer[normalizedName] = actionDef;
        } else {
          // Update runAfter references if they point to the old name
          const runAfter = actionDef.runAfter;
          if (runAfter && runAfter[plainOldName]) {
            const { [plainOldName]: oldStatus, ...rest } = runAfter;
            newContainer[actionName] = {
              ...actionDef,
              runAfter: { ...rest, [normalizedName]: oldStatus },
            };
          } else {
            newContainer[actionName] = actionDef;
          }
        }
      }
      return newContainer;
    };

    // Rewrite downstream expression references (outputs/body/items/…) that point
    // at the old action name so they follow the rename. This also
    // returns a fresh deep copy, which we own and can safely mutate below — so
    // the nested path no longer needs a separate JSON clone.
    const referencedActions = updateActionReferences(
      workflow.definition.actions,
      plainOldName,
      normalizedName,
    );

    // Apply the key rename (and runAfter fixups) on the owned copy.
    let newTopActions: Record<string, Action>;
    const isTopLevel = location.container === workflow.definition.actions;

    if (isTopLevel) {
      newTopActions = renameInContainer(referencedActions);
    } else {
      newTopActions = referencedActions;
      // Navigate to the parent container using the path from findActionRecursive
      // path looks like: "actions.ForEach_1.actions" — strip the leading "actions." segment
      const segments = location.path.split('.').slice(1); // skip leading "actions"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let target: any = newTopActions;
      for (const seg of segments) {
        target = target[seg];
      }
      // target is now the container with the action to rename
      const renamed = renameInContainer(target as Record<string, Action>);
      // Replace the container contents in-place
      for (const key of Object.keys(target)) {
        delete target[key];
      }
      Object.assign(target, renamed);
    }

    const newWorkflow: WorkflowDefinition = {
      ...workflow,
      definition: {
        ...workflow.definition,
        actions: newTopActions,
        // The workflow-level `outputs` section can also reference action outputs
        // (e.g. `{ result: { value: "@body('action')" } }`), so rewrite those
        // references too. Only spread the key when present to avoid introducing
        // an explicit `outputs: undefined`.
        ...(workflow.definition.outputs !== undefined && {
          outputs: updateActionReferences(
            workflow.definition.outputs,
            plainOldName,
            normalizedName,
          ),
        }),
      },
    };

    // Determine scoped node IDs — for nested actions, IDs use the scopedId format
    const scopeCtx = findScopeContext(plainOldName, workflow.definition.actions);
    const oldNodeId = scopeCtx
      ? `action-${scopeCtx.parentActionName}/${plainOldName}`
      : `action-${plainOldName}`;
    const newNodeId = scopeCtx
      ? `action-${scopeCtx.parentActionName}/${normalizedName}`
      : `action-${normalizedName}`;

    // Update connection store mapping (move reference key from old node ID to new)
    const connectionStore = useConnectionStore.getState();
    const referenceKey = connectionStore.mapping[oldNodeId];
    if (referenceKey) {
      connectionStore.removeNodeConnection(oldNodeId);
      connectionStore.setNodeConnection(newNodeId, referenceKey);
    }

    // Update selection if the renamed action was selected
    let newSelectedNode = selectedNode;
    if (selectedNode?.type === 'action' && (selectedNode.name === plainOldName || selectedNode.name === oldName)) {
      newSelectedNode = {
        ...selectedNode,
        id: newNodeId,
        name: normalizedName,
      };
    }

    set({
      workflow: newWorkflow,
      workflowJson: JSON.stringify(newWorkflow, null, 2),
      selectedNode: newSelectedNode,
      isDirty: true, changeCount: get().changeCount + 1,
    });

    return { isValid: true, normalizedName };
  },

  /**
   * Rename a trigger in the workflow.
   * Updates any actions that have runAfter references to triggers (though typically
   * actions run after triggers have empty runAfter).
   *
   * @param oldName - Current name of the trigger
   * @param newName - New name for the trigger
   * @returns Validation result with normalized name if successful
   */
  renameTrigger: (oldName: string, newName: string): NameValidationResult => {
    const { workflow, selectedNode } = get();
    if (!workflow) {
      return { isValid: false, error: 'No workflow loaded' };
    }

    const trigger = workflow.definition.triggers[oldName];
    if (!trigger) {
      return { isValid: false, error: `Trigger "${oldName}" not found` };
    }

    // Validate the new name
    const validation = validateTriggerName(newName, workflow, oldName);
    if (!validation.isValid || !validation.normalizedName) {
      return validation;
    }

    const normalizedName = validation.normalizedName;

    // If name hasn't changed, nothing to do
    if (normalizedName === oldName) {
      return { isValid: true, normalizedName };
    }

    // Create new triggers map with renamed trigger
    const newTriggers: Record<string, Trigger> = {};

    for (const [triggerName, triggerDef] of Object.entries(workflow.definition.triggers)) {
      if (triggerName === oldName) {
        newTriggers[normalizedName] = triggerDef;
      } else {
        newTriggers[triggerName] = triggerDef;
      }
    }

    const newWorkflow: WorkflowDefinition = {
      ...workflow,
      definition: {
        ...workflow.definition,
        triggers: newTriggers,
      },
    };

    // Update connection store mapping (move reference key from old node ID to new)
    const oldNodeId = `trigger-${oldName}`;
    const newNodeId = `trigger-${normalizedName}`;
    const connectionStore = useConnectionStore.getState();
    const referenceKey = connectionStore.mapping[oldNodeId];
    if (referenceKey) {
      connectionStore.removeNodeConnection(oldNodeId);
      connectionStore.setNodeConnection(newNodeId, referenceKey);
    }

    // Update selection if the renamed trigger was selected
    let newSelectedNode = selectedNode;
    if (selectedNode?.type === 'trigger' && selectedNode.name === oldName) {
      newSelectedNode = {
        ...selectedNode,
        id: newNodeId,
        name: normalizedName,
      };
    }

    set({
      workflow: newWorkflow,
      workflowJson: JSON.stringify(newWorkflow, null, 2),
      selectedNode: newSelectedNode,
      isDirty: true, changeCount: get().changeCount + 1,
    });

    return { isValid: true, normalizedName };
  },

  /**
   * Rename a tool within an agent.
   *
   * @param agentName - Name of the agent containing the tool
   * @param oldToolName - Current name of the tool
   * @param newToolName - New name for the tool
   * @returns Validation result with normalized name if successful
   */
  renameTool: (agentName: string, oldToolName: string, newToolName: string): NameValidationResult => {
    const { workflow, selectedNode, selectedNodes, mockedNodes } = get();
    if (!workflow) {
      return { isValid: false, error: 'No workflow loaded' };
    }

    const prepared = prepareAgentActionUpdate(workflow, agentName);
    if (!prepared.ok) {
      return { isValid: false, error: prepared.error };
    }
    const agentAction = prepared.location.action;

    const tool = agentAction.tools?.[oldToolName];
    if (!tool) {
      return { isValid: false, error: `Tool "${oldToolName}" not found in agent "${agentName}"` };
    }

    const validation = validateToolName(
      newToolName,
      Object.keys(agentAction.tools ?? {}),
      oldToolName,
    );
    if (!validation.isValid || !validation.normalizedName) {
      return validation;
    }
    const normalized = validation.normalizedName;
    const innerActionName = Object.keys(tool.actions ?? {})[0];

    // If name hasn't changed, nothing to do
    if (normalized === oldToolName) {
      return { isValid: true, normalizedName: normalized };
    }

    // Actionless tools use their outer name in the canvas node ID. Prevent that
    // name from colliding with a sibling tool's effective canvas identity.
    if (!innerActionName) {
      const normalizedLower = normalized.toLowerCase();
      const hasCanvasNameConflict = Object.entries(agentAction.tools ?? {}).some(
        ([toolName, siblingTool]) => {
          if (toolName === oldToolName) {
            return false;
          }
          const siblingInnerActionName = Object.keys(siblingTool.actions ?? {})[0];
          return (siblingInnerActionName ?? toolName).toLowerCase() === normalizedLower;
        },
      );
      if (hasCanvasNameConflict) {
        return {
          isValid: false,
          error: `A tool or tool action with name '${normalized}' already exists`,
        };
      }
    }

    // Create new tools map with renamed tool
    const newTools: Record<string, AgentTool> = {};
    for (const [toolName, toolDef] of Object.entries(agentAction.tools || {})) {
      if (toolName === oldToolName) {
        newTools[normalized] = toolDef;
      } else {
        newTools[toolName] = toolDef;
      }
    }

    // Update the agent with renamed tool
    const updatedAgent = {
      ...agentAction,
      tools: newTools,
    };

    prepared.location.container[prepared.actionName] = updatedAgent;

    // Action-backed tool IDs use the inner action name, while actionless MCP
    // tool IDs use the outer tool name and must move when that name changes.
    const oldNodeId = `tool-${prepared.actionName}-${innerActionName ?? oldToolName}`;
    const newNodeId = `tool-${prepared.actionName}-${innerActionName ?? normalized}`;
    const scopedAgentName = prepared.location.scopedName;
    const isRenamedToolSelection = (node: SelectedNode): boolean =>
      node.type === 'tool' &&
      (node.parentAgentName === prepared.actionName || node.parentAgentName === scopedAgentName) &&
      node.toolName === oldToolName;
    let newSelectedNode = selectedNode;
    if (selectedNode && isRenamedToolSelection(selectedNode)) {
      newSelectedNode = {
        ...selectedNode,
        id: newNodeId,
        name: normalized,
        toolName: normalized,
      };
    }

    const newSelectedNodes = selectedNodes.map((node) =>
      isRenamedToolSelection(node)
        ? { ...node, id: newNodeId, name: normalized, toolName: normalized }
        : node
    );

    let newMockedNodes = mockedNodes;
    if (oldNodeId !== newNodeId) {
      const connectionStore = useConnectionStore.getState();
      const referenceKey = connectionStore.mapping[oldNodeId];
      if (referenceKey) {
        connectionStore.removeNodeConnection(oldNodeId);
        connectionStore.setNodeConnection(newNodeId, referenceKey);
      }

      const mockedNode = mockedNodes[oldNodeId];
      if (mockedNode) {
        const { [oldNodeId]: _removed, ...remainingMockedNodes } = mockedNodes;
        newMockedNodes = {
          ...remainingMockedNodes,
          [newNodeId]: 'actionName' in mockedNode
            ? { ...mockedNode, actionName: normalized }
            : mockedNode,
        };
      }
    }

    const identity = get().currentWorkflowIdentity;
    if (identity) {
      const layoutStore = useWorkflowLayoutStore.getState();
      const position = layoutStore.getToolNodePositions(
        identity,
        get().currentMode,
        prepared.actionName,
      )[oldToolName];
      if (position) {
        layoutStore.setToolNodePosition(
          identity,
          get().currentMode,
          prepared.actionName,
          normalized,
          position,
        );
        layoutStore.removeTool(
          identity,
          get().currentMode,
          prepared.actionName,
          oldToolName,
        );
      }
    }

    set({
      workflow: prepared.workflow,
      workflowJson: JSON.stringify(prepared.workflow, null, 2),
      selectedNode: newSelectedNode,
      selectedNodes: newSelectedNodes,
      mockedNodes: newMockedNodes,
      isDirty: true, changeCount: get().changeCount + 1,
    });

    return { isValid: true, normalizedName: normalized };
  },

  /**
   * Rename an inner action within a tool
   * @param agentName - Name of the parent agent
   * @param toolName - Name of the tool containing the action
   * @param oldActionName - Current name of the inner action
   * @param newActionName - New name for the inner action
   * @returns Validation result with normalized name if successful
   */
  renameToolInnerAction: (agentName: string, toolName: string, oldActionName: string, newActionName: string): NameValidationResult => {
    const { workflow, selectedNode } = get();
    if (!workflow) {
      return { isValid: false, error: 'No workflow loaded' };
    }

    const prepared = prepareAgentActionUpdate(workflow, agentName);
    if (!prepared.ok) {
      return { isValid: false, error: prepared.error };
    }
    const agentAction = prepared.location.action;

    const tool = agentAction.tools?.[toolName];
    if (!tool) {
      return { isValid: false, error: `Tool "${toolName}" not found in agent "${agentName}"` };
    }

    const innerAction = tool.actions?.[oldActionName];
    if (!innerAction) {
      return { isValid: false, error: `Action "${oldActionName}" not found in tool "${toolName}"` };
    }

    // Validate the new name using comprehensive validation
    const validation = validateToolInnerActionName(newActionName, workflow, oldActionName);
    if (!validation.isValid || !validation.normalizedName) {
      return validation;
    }

    const normalized = validation.normalizedName;

    // If name hasn't changed, nothing to do
    if (normalized === oldActionName) {
      return { isValid: true, normalizedName: normalized };
    }

    // Create new actions map with renamed action and updated runAfter references
    const newToolActions: Record<string, Action> = {};
    for (const [actionName, actionDef] of Object.entries(tool.actions || {})) {
      if (actionName === oldActionName) {
        // Rename the action
        newToolActions[normalized] = actionDef;
      } else {
        // Update runAfter references if they point to the old action name
        const runAfter = actionDef.runAfter;
        if (runAfter && oldActionName in runAfter) {
          const { [oldActionName]: oldStatus, ...rest } = runAfter;
          newToolActions[actionName] = {
            ...actionDef,
            runAfter: { ...rest, [normalized]: oldStatus },
          };
        } else {
          newToolActions[actionName] = actionDef;
        }
      }
    }

    // Update the tool with renamed action
    const updatedTool: AgentTool = {
      ...tool,
      actions: newToolActions,
    };

    // Update the agent with the modified tool
    const updatedAgent = {
      ...agentAction,
      tools: {
        ...agentAction.tools,
        [toolName]: updatedTool,
      },
    };

    prepared.location.container[prepared.actionName] = updatedAgent;

    // Update selection if the renamed action's tool was selected.
    // Note: workflowStore's selectedNode may not have innerActionName (it's populated by nodeEditorStore sync),
    // so we just check agent and tool match. The canvas tool-node id is keyed
    // by the inner action name, so an inner-action rename moves the node to a
    // new id — keep selection in sync.
    let newSelectedNode = selectedNode;
    if (selectedNode?.type === 'tool' &&
        selectedNode.parentAgentName === prepared.actionName &&
        selectedNode.toolName === toolName) {
      newSelectedNode = {
        ...selectedNode,
        id: `tool-${prepared.actionName}-${normalized}`,
        innerActionName: normalized,
      };
    }

    set({
      workflow: prepared.workflow,
      workflowJson: JSON.stringify(prepared.workflow, null, 2),
      selectedNode: newSelectedNode,
      isDirty: true, changeCount: get().changeCount + 1,
    });

    return { isValid: true, normalizedName: normalized };
  },

  updateSwitchCaseValue: (switchName: string, caseName: string, newValue: string) => {
    const { workflow } = get();
    if (!workflow) return;

    // Use recursive search to find switch actions nested inside scopes
    const location = findActionRecursive(workflow.definition.actions, switchName);
    if (!location) return;

    const action = location.action;
    if (!isSwitchAction(action)) return;

    const switchAction = action as SwitchAction;
    const caseData = switchAction.cases?.[caseName];
    if (!caseData) return;

    // Preserve numeric type if the original case value was a number
    const typedValue: string | number =
      typeof caseData.case === 'number' && !isNaN(Number(newValue))
        ? Number(newValue)
        : newValue;

    // Update via the container reference found by recursive search
    location.container[switchName] = {
      ...switchAction,
      cases: {
        ...switchAction.cases,
        [caseName]: {
          ...caseData,
          case: typedValue,
        },
      },
    };

    const newWorkflow: WorkflowDefinition = {
      ...workflow,
      definition: {
        ...workflow.definition,
        actions: { ...workflow.definition.actions },
      },
    };

    set({
      workflow: newWorkflow,
      workflowJson: JSON.stringify(newWorkflow, null, 2),
      isDirty: true,
      changeCount: get().changeCount + 1,
    });
  },

  /**
   * Generate a unique action name by appending a number suffix if needed
   */
  generateUniqueActionName: (baseName: string) => {
    const { workflow } = get();
    if (!workflow) return baseName;

    // Collect all action names recursively (including inside scopes)
    const allActionNames = collectAllActionNames(workflow.definition.actions ?? {});
    const existingTriggers = workflow.definition.triggers;

    // Check if the base name is available
    if (!allActionNames.has(baseName) && !existingTriggers[baseName]) {
      return baseName;
    }

    // Find the next available number suffix
    let counter = 1;
    let candidateName = `${baseName}_${counter}`;
    while (allActionNames.has(candidateName) || existingTriggers[candidateName]) {
      counter++;
      candidateName = `${baseName}_${counter}`;
    }

    return candidateName;
  },

  /**
   * Generate a unique tool name within an agent
   */
  generateUniqueToolName: (agentName: string, baseName: string) => {
    const { workflow } = get();
    if (!workflow) return baseName;

    const resolution = resolveAgentActionLocation(workflow.definition.actions, agentName);
    if (!resolution.ok) {
      notifyAgentToolError(resolution.error);
      return baseName;
    }

    const existingTools = resolution.location.action.tools ?? {};

    // Check if the base name is available
    if (!existingTools[baseName]) {
      return baseName;
    }

    // Find the next available number suffix
    let counter = 1;
    let candidateName = `${baseName}_${counter}`;
    while (existingTools[candidateName]) {
      counter++;
      candidateName = `${baseName}_${counter}`;
    }

    return candidateName;
  },

  /**
   * Add an action to the workflow at the specified insertion point
   * This handles updating runAfter dependencies to maintain the workflow chain
   * If no insertion point is provided, adds a floating action with no dependencies
   */
  addAction: (name: string, action: Action, insertionPoint?: InsertionPoint) => {
    const { workflow } = get();
    if (!workflow) return;

    // Clone the actions
    let newActions = { ...workflow.definition.actions };

    if (!insertionPoint) {
      // Add floating action with no dependencies (empty runAfter)
      // Mark as detached to prevent auto-connection to triggers
      const newAction: Action = {
        ...action,
        runAfter: {},
        detached: true,
      };
      newActions[name] = newAction;
    } else if (insertionPoint.scope) {
      // Scoped insertion: add action inside a control flow scope (e.g., Foreach loop body)
      const { parentActionName, branchType } = insertionPoint.scope;
      // If sourceNodeId points to a different action (not trigger, not scope parent),
      // use it to set runAfter (e.g., adding after Compose inside a ForEach)
      const sourceId = insertionPoint.sourceNodeId;
      const isActionSource = sourceId?.startsWith('action-');
      const rawSourceName = isActionSource ? sourceId.replace(/^action-/, '') : undefined;
      // Strip scope prefix — scoped node IDs are "Parent/Child" but runAfter uses plain names
      const sourceActionName = rawSourceName?.includes('/') ? rawSourceName.split('/').pop()! : rawSourceName;
      // When targetNodeId is also present, we're inserting at the START of a branch:
      // the new action becomes the branch root (runAfter = {}) and the existing first
      // child is re-parented to run after the new action.
      const rawTargetScoped = insertionPoint.targetNodeId?.startsWith('action-')
        ? insertionPoint.targetNodeId.slice('action-'.length)
        : insertionPoint.targetNodeId;
      const targetActionName = rawTargetScoped?.includes('/') ? rawTargetScoped.split('/').pop()! : rawTargetScoped;
      const hasRunAfterSource = sourceActionName && sourceActionName !== parentActionName;
      const newAction: Action = {
        ...action,
        runAfter: targetActionName ? {} : (hasRunAfterSource ? { [sourceActionName]: ['Succeeded'] } : {}),
      };

      // Build a branch actions record with the new action first, and rewrite the
      // target's runAfter to depend on the new action when inserting at start.
      const buildBranchActions = (existing: Record<string, Action> | undefined): Record<string, Action> => {
        const base = existing ?? {};
        if (!targetActionName || !(targetActionName in base)) {
          return { ...base, [name]: newAction };
        }
        const rebuilt: Record<string, Action> = {};
        rebuilt[name] = newAction;
        for (const [k, v] of Object.entries(base)) {
          if (k === targetActionName) {
            rebuilt[k] = { ...v, runAfter: { ...(v.runAfter ?? {}), [name]: ['Succeeded'] } };
          } else {
            rebuilt[k] = v;
          }
        }
        return rebuilt;
      };

      // Block nested ForEach: prevent adding a ForEach inside any scope that is itself inside a ForEach
      if (isForeachAction(action)) {
        const isDirectForEachTarget = branchType === 'loop';
        const scopeCtx = !isDirectForEachTarget ? findScopeContext(parentActionName, newActions) : undefined;
        if (isDirectForEachTarget || scopeCtx) {
          console.warn(`Cannot nest ForEach inside ForEach "${isDirectForEachTarget ? parentActionName : scopeCtx?.parentActionName}"`);
          notifyNestedForEachBlocked();
          return;
        }
      }

      // Helper to add action into nested scope.
      // Returns the original reference when no insertion was made so callers
      // can use reference equality to detect changes.
      const addToScope = (actions: Record<string, Action>): Record<string, Action> => {
        for (const [actionName, act] of Object.entries(actions)) {
          if (actionName === parentActionName) {
            const result = { ...actions };
            if (isForeachAction(act) && branchType === 'loop') {
              result[actionName] = {
                ...act,
                actions: buildBranchActions(act.actions),
              } as Action;
              return result;
            }
            if (isUntilAction(act) && branchType === 'loop') {
              result[actionName] = {
                ...act,
                actions: buildBranchActions(act.actions),
              } as Action;
              return result;
            }
            if (isIfAction(act) && (branchType === 'true' || branchType === 'false')) {
              const ifAction = act as IfAction;
              if (branchType === 'true') {
                result[actionName] = {
                  ...ifAction,
                  actions: buildBranchActions(ifAction.actions),
                } as Action;
              } else {
                result[actionName] = {
                  ...ifAction,
                  else: { ...(ifAction.else ?? {}), actions: buildBranchActions(ifAction.else?.actions) },
                } as Action;
              }
              return result;
            }
            if (isSwitchAction(act)) {
              const switchAction = act as SwitchAction;
              if (branchType === 'default') {
                result[actionName] = {
                  ...switchAction,
                  default: { ...(switchAction.default ?? {}), actions: buildBranchActions(switchAction.default?.actions) },
                } as Action;
              } else if (branchType.startsWith('case-') && switchAction.cases) {
                const caseName = branchType.replace('case-', '');
                const caseData = switchAction.cases[caseName] as { case: string; actions?: Record<string, Action> } | undefined;
                if (caseData) {
                  result[actionName] = {
                    ...switchAction,
                    cases: {
                      ...switchAction.cases,
                      [caseName]: { ...caseData, actions: buildBranchActions(caseData.actions) },
                    },
                  } as Action;
                  return result;
                }
              }
              // Switch action found but branch didn't match — return unchanged
              return actions;
            }
            // Target found but type didn't match any branch — return unchanged
            return actions;
          }
          // Recursively search nested scopes
          // Collect all child action containers with their rebuild functions
          const childSlots: Array<[Record<string, Action>, (updated: Record<string, Action>) => Action]> = [];

          if (isForeachAction(act)) {
            const fa = act as ForeachAction;
            if (fa.actions) {
              childSlots.push([fa.actions, (updated) => ({ ...fa, actions: updated }) as Action]);
            }
          }
          if (isUntilAction(act)) {
            const ua = act as UntilAction;
            if (ua.actions) {
              childSlots.push([ua.actions, (updated) => ({ ...ua, actions: updated }) as Action]);
            }
          }
          if (isIfAction(act)) {
            const ia = act as IfAction;
            if (ia.actions) {
              childSlots.push([ia.actions, (updated) => ({ ...ia, actions: updated }) as Action]);
            }
            if (ia.else?.actions) {
              childSlots.push([ia.else.actions, (updated) => ({ ...ia, else: { ...ia.else, actions: updated } }) as Action]);
            }
          }
          if (isSwitchAction(act)) {
            const sa = act as SwitchAction;
            if (sa.cases) {
              for (const [cn, cd] of Object.entries(sa.cases)) {
                const tc = cd as { case: string; actions?: Record<string, Action> };
                if (tc.actions) {
                  childSlots.push([tc.actions, (updated) => ({ ...sa, cases: { ...sa.cases, [cn]: { ...tc, actions: updated } } }) as Action]);
                }
              }
            }
            if (sa.default?.actions) {
              childSlots.push([sa.default.actions, (updated) => ({ ...sa, default: { ...sa.default, actions: updated } }) as Action]);
            }
          }

          for (const [childActions, rebuild] of childSlots) {
            const nested = addToScope(childActions);
            if (nested !== childActions) {
              return { ...actions, [actionName]: rebuild(nested) };
            }
          }
        }
        return actions;
      };

      newActions = addToScope(newActions);
    } else {
      const { sourceNodeId, targetNodeId } = insertionPoint;

      const isTriggerSource = sourceNodeId.startsWith('trigger-');

      // Node IDs use scoped path form for nested actions
      // (e.g. "action-Condition/HTTP_5"). Strip the prefix and take the
      // leaf segment — runAfter keys must be the plain action name (action
      // names are workflow-globally unique).
      const rawSource = isTriggerSource
        ? sourceNodeId.slice('trigger-'.length)
        : sourceNodeId.slice('action-'.length);
      const sourceName = rawSource.includes('/') ? rawSource.split('/').pop()! : rawSource;

      const rawTarget = targetNodeId?.startsWith('action-')
        ? targetNodeId.slice('action-'.length)
        : targetNodeId;
      const targetName = rawTarget?.includes('/') ? rawTarget.split('/').pop()! : rawTarget;

      // Build the new action with runAfter pointing to source
      // If source is a trigger, runAfter should be empty (triggers don't have runAfter)
      const newAction: Action = {
        ...action,
        runAfter: isTriggerSource ? {} : { [sourceName]: ['Succeeded'] },
      };

      // Locate the source's container so the new action lands in the
      // correct scope. Without this, inserting between two actions inside
      // an If/Foreach/Switch branch would silently drop the new action at
      // the top level, producing a malformed workflow.
      const sourceLocation = isTriggerSource
        ? null
        : findActionRecursive(newActions, sourceName);
      const isNested = sourceLocation && sourceLocation.container !== newActions;

      if (isNested) {
        // Source is inside a scope. Deep-clone the actions tree so we can
        // mutate the nested container without sharing references with the
        // previous workflow state, then re-locate the (now-cloned) source
        // container and write into it. Prefer structuredClone (handles
        // more types, no JSON round-trip), fall back to JSON for older
        // runtimes, and bail out on failure rather than crashing the UI.
        let clonedActions: Record<string, Action> | null = null;
        try {
          clonedActions =
            typeof structuredClone === 'function'
              ? (structuredClone(newActions) as Record<string, Action>)
              : (JSON.parse(JSON.stringify(newActions)) as Record<string, Action>);
        } catch (error) {
          console.error('[addAction] Failed to clone workflow actions for nested insertion:', error);
        }

        if (!clonedActions) {
          // Cloning failed — abort the insertion rather than mutating the
          // existing tree (which would break Zustand reference equality
          // and could leave the workflow in an inconsistent state).
          return;
        }

        newActions = clonedActions;
        const clonedLocation = findActionRecursive(newActions, sourceName);
        const container = clonedLocation?.container ?? newActions;
        container[name] = newAction;

        if (targetName) {
          const targetAction = container[targetName];
          if (targetAction) {
            const newRunAfter: RunAfter = { ...targetAction.runAfter };
            if (!isTriggerSource && newRunAfter[sourceName]) {
              delete newRunAfter[sourceName];
            }
            newRunAfter[name] = ['Succeeded'];
            container[targetName] = { ...targetAction, runAfter: newRunAfter };
          }
        }
      } else {
        // Top-level source (or trigger): write directly into the cloned
        // top-level actions record.
        newActions[name] = newAction;

        if (targetName) {
          const targetAction = newActions[targetName];
          if (targetAction) {
            const newRunAfter: RunAfter = { ...targetAction.runAfter };
            if (!isTriggerSource && newRunAfter[sourceName]) {
              delete newRunAfter[sourceName];
            }
            newRunAfter[name] = ['Succeeded'];
            newActions[targetName] = { ...targetAction, runAfter: newRunAfter };
          }
        }
      }
    }

    // Auto-layout — push downstream + local re-space if needed. The
    // orchestrator handles every gate (manual mode, end-of-chain, midpoint
    // placement) and the two-phase pipeline so `addAction` stays a thin
    // shell. Scope-bounded: never touches sibling scopes or the rest of
    // the graph.
    //
    // Positions live in the client-side layout store, but
    // `layoutAfterInsert` reads them as transient `metadata.position`
    // fields. Inject them into the actions tree (and the workflow's
    // triggers) before calling the helper, then extract the result back
    // out and strip the transient fields so the saved workflow stays
    // position-free.
    const identity = get().currentWorkflowIdentity;
    const mode = get().currentMode;
    const storeLayout = identity
      ? useWorkflowLayoutStore.getState().getNodePositions(identity, mode)
      : {};
    const actionsWithPositions = injectActionPositions(newActions, storeLayout);
    const workflowWithPositions: WorkflowDefinition = {
      ...workflow,
      definition: {
        ...workflow.definition,
        triggers: injectTriggerPositions(workflow.definition.triggers, storeLayout),
      },
    };

    const finalActionsWithPositions = layoutAfterInsert({
      actions: actionsWithPositions,
      workflow: workflowWithPositions,
      insertionPoint,
      insertedName: name,
      orientation: get().layoutOrientation,
    });

    if (identity) {
      const updatedPositions = extractActionPositions(finalActionsWithPositions);
      if (Object.keys(updatedPositions).length > 0) {
        useWorkflowLayoutStore.getState().applyNodePositions(identity, mode, updatedPositions);
      }
    }

    const finalActions = stripActionPositions(finalActionsWithPositions);

    const newWorkflow: WorkflowDefinition = {
      ...workflow,
      definition: {
        ...workflow.definition,
        actions: finalActions,
      },
    };

    set({
      workflow: newWorkflow,
      workflowJson: JSON.stringify(newWorkflow, null, 2),
      isDirty: true, changeCount: get().changeCount + 1,
    });

    // Mirror any auto-matched connection reference baked into the action's
    // inputs into the connection store mapping. Mirrors the addTrigger path.
    // For scoped inserts (inside If/Switch/Foreach/Until/Scope), the canvas
    // node id uses the full slash-joined path; walk the freshly-set workflow
    // to find the leaf action's canonical nodeId.
    const actionRefKey = extractReferenceKeyFromOperation(action as unknown as Record<string, unknown>);
    if (actionRefKey) {
      let nodeId = `action-${name}`;
      if (insertionPoint?.scope) {
        let scopedNodeId: string | null = null;
        walkAllActions(finalActions as unknown as Record<string, unknown>, (_names, _act, scopedName, walkedNodeId) => {
          // Prefer a nested match: leaf segment equals the new action's name
          // and the path has more than one segment.
          if (scopedNodeId) return;
          if (scopedName === name) return; // top-level — skip; scoped wins
          if (scopedName.endsWith(`/${name}`)) {
            scopedNodeId = walkedNodeId;
          }
        });
        if (scopedNodeId) nodeId = scopedNodeId;
      }
      useConnectionStore.getState().setNodeConnection(nodeId, actionRefKey);
    }
  },

  /**
   * Duplicate an action in the workflow
   * Creates a copy with a unique name and positions it near the original
   * Works for both top-level and nested actions (inside If/Foreach/Switch blocks)
   * Returns the new action name, or null if duplication failed
   */
  duplicateAction: (name: string) => {
    const { workflow, generateUniqueActionName } = get();
    if (!workflow) return null;

    // Strip scope prefix if present (e.g., "Until/Increment_variable" → "Increment_variable")
    const actionName = name.includes('/') ? name.split('/').pop()! : name;

    // Find the action - could be top-level or nested
    const found = findActionRecursive(workflow.definition.actions, actionName);
    if (!found) return null;

    const originalAction = found.action;

    // Generate a unique name for the duplicate
    const newName = generateUniqueActionName(actionName);

    // Deep clone the action
    const duplicatedAction: Action = JSON.parse(JSON.stringify(originalAction));

    // Offset the position slightly so it's visible
    if (duplicatedAction.metadata?.['position']) {
      const pos = duplicatedAction.metadata['position'] as { x: number; y: number };
      duplicatedAction.metadata = {
        ...duplicatedAction.metadata,
        position: { x: pos.x + 50, y: pos.y + 50 },
      };
    }

    // Add the duplicated action at the same level as the original
    const newActions = addActionAtSameLevel(
      workflow.definition.actions,
      actionName,
      newName,
      duplicatedAction
    );

    const newWorkflow: WorkflowDefinition = {
      ...workflow,
      definition: {
        ...workflow.definition,
        actions: newActions,
      },
    };

    set({
      workflow: newWorkflow,
      workflowJson: JSON.stringify(newWorkflow, null, 2),
      isDirty: true, changeCount: get().changeCount + 1,
    });

    return newName;
  },

  /**
   * Paste an action from clipboard data (deep-cloned snapshot).
   * Unlike duplicateAction which finds the action by name in the workflow,
   * this inserts the provided action data directly — works even if the
   * original was deleted (cut) or modified after copy.
   */
  pasteActionFromClipboard: (action: Action, originalName: string) => {
    const { workflow } = get();
    if (!workflow) return null;

    // Check uniqueness against all action names including nested scopes
    const allNames = collectAllActionNames(workflow.definition.actions);
    const triggerNames = new Set(Object.keys(workflow.definition.triggers));
    let newName = originalName;
    if (allNames.has(newName) || triggerNames.has(newName)) {
      let counter = 1;
      newName = `${originalName}_${counter}`;
      while (allNames.has(newName) || triggerNames.has(newName)) {
        counter++;
        newName = `${originalName}_${counter}`;
      }
    }

    const pastedAction: Action = JSON.parse(JSON.stringify(action));

    // Offset position so it's visible
    if (pastedAction.metadata?.['position']) {
      const pos = pastedAction.metadata['position'] as { x: number; y: number };
      pastedAction.metadata = {
        ...pastedAction.metadata,
        position: { x: pos.x + 50, y: pos.y + 50 },
      };
    }

    // Clear runAfter and mark as detached to prevent auto-connection to triggers
    pastedAction.runAfter = {};
    pastedAction.detached = true;

    // Add at top level
    const newActions = { ...workflow.definition.actions, [newName]: pastedAction };

    const newWorkflow: WorkflowDefinition = {
      ...workflow,
      definition: {
        ...workflow.definition,
        actions: newActions,
      },
    };

    set({
      workflow: newWorkflow,
      workflowJson: JSON.stringify(newWorkflow, null, 2),
      isDirty: true, changeCount: get().changeCount + 1,
    });

    return newName;
  },

  /**
   * Delete an action from the workflow
   * This handles updating runAfter dependencies to maintain the workflow chain
   * and cleans up any associated connection references
   * Works for both top-level and nested actions (inside If/Foreach/Switch blocks)
   */
  deleteAction: (name: string) => {
    const { workflow } = get();
    if (!workflow) return;

    // Strip scope prefix if present (e.g., "Until/Increment_variable" → "Increment_variable")
    // Scoped node IDs use "ParentScope/ChildName" format but the action key is just the child name
    const actionName = name.includes('/') ? name.split('/').pop()! : name;

    // Find the action - could be top-level or nested
    const found = findActionRecursive(workflow.definition.actions, actionName);
    if (!found) return;

    const deletedAction = found.action;

    // Get the runAfter dependencies of the deleted action
    const deletedRunAfter = deletedAction.runAfter || {};

    // Phase 1 (symmetric collapse) — pull every transitive downstream sibling
    // of the deleted action back toward the source. Runs BEFORE the delete so
    // `runAfter` references to the doomed action are still intact for the
    // traversal. No-op when the workflow opts into manual layout mode.
    //
    // The collapse helper still reads positions as transient
    // `metadata.position`, so inject them from the layout store first, then
    // extract the result back into the store and strip the transient fields
    // before saving the workflow.
    const identity = get().currentWorkflowIdentity;
    const mode = get().currentMode;
    const storeLayout = identity
      ? useWorkflowLayoutStore.getState().getNodePositions(identity, mode)
      : {};
    const actionsWithPositions = injectActionPositions(workflow.definition.actions, storeLayout);

    const collapsedWithPositions = getLayoutMode(workflow) === 'manual'
      ? actionsWithPositions
      : layoutAfterDelete(actionsWithPositions, actionName, get().layoutOrientation);

    if (identity) {
      const updatedPositions = extractActionPositions(collapsedWithPositions);
      if (Object.keys(updatedPositions).length > 0) {
        useWorkflowLayoutStore.getState().applyNodePositions(identity, mode, updatedPositions);
      }
      // Drop the deleted node + any tools owned by it from the layout store.
      useWorkflowLayoutStore.getState().removeNode(identity, mode, `action-${actionName}`);
      if (isAgentAction(deletedAction)) {
        useWorkflowLayoutStore.getState().removeAgent(identity, mode, actionName);
      }
    }

    const collapsed = stripActionPositions(collapsedWithPositions);

    // Delete the action recursively (handles nested structures)
    let newActions = deleteActionRecursive(collapsed, actionName);

    // Reform runAfter dependencies recursively:
    // 1. Remove the deleted action from all runAfter entries that reference it
    // 2. Add the deleted action's predecessors to those successors to maintain edges
    newActions = reformRunAfterOnDeleteRecursive(newActions, actionName, deletedRunAfter);

    const newWorkflow: WorkflowDefinition = {
      ...workflow,
      definition: {
        ...workflow.definition,
        actions: newActions,
      },
    };

    // Clean up any connection references for this node
    const nodeId = `action-${name}`;
    useConnectionStore.getState().removeNodeConnection(nodeId);

    // Drop any sidecar mock entry so it isn't orphaned in `mockedNodes`.
    // Mocks are keyed by the stripped action name, not the scoped node id.
    get().removeMockedNode(`action-${actionName}`);

    set({
      workflow: newWorkflow,
      workflowJson: JSON.stringify(newWorkflow, null, 2),
      isDirty: true, changeCount: get().changeCount + 1,
    });
  },

  /**
   * Delete a trigger from the workflow
   * Also cleans up any associated connection references
   * Reverts workflow kind to Stateful if deleting an agent trigger
   */
  deleteTrigger: (name: string) => {
    const { workflow } = get();
    if (!workflow) return;

    // Check if the trigger being deleted is an agent trigger
    const trigger = workflow.definition.triggers[name];
    const wasAgentTrigger = trigger?.type === 'Request' && trigger?.kind === 'Agent';

    const newTriggers = { ...workflow.definition.triggers };
    delete newTriggers[name];

    // Revert to Stateful if we removed the agent trigger
    const newKind = wasAgentTrigger ? 'Stateful' : workflow.kind;

    const newWorkflow: WorkflowDefinition = {
      ...workflow,
      kind: newKind,
      definition: {
        ...workflow.definition,
        triggers: newTriggers,
      },
    };

    // Clean up any connection references for this node
    const nodeId = `trigger-${name}`;
    useConnectionStore.getState().removeNodeConnection(nodeId);

    // Drop any sidecar mock entry so it isn't orphaned in `mockedNodes`.
    get().removeMockedNode(nodeId);

    // Drop the trigger's stored layout position.
    const triggerIdentity = get().currentWorkflowIdentity;
    if (triggerIdentity) {
      useWorkflowLayoutStore.getState().removeNode(triggerIdentity, get().currentMode, nodeId);
    }

    set({
      workflow: newWorkflow,
      workflowJson: JSON.stringify(newWorkflow, null, 2),
      isDirty: true, changeCount: get().changeCount + 1,
    });
  },

  /**
   * Add a tool to an agent
   */
  addToolToAgent: (agentName: string, toolName: string, tool: AgentTool) => {
    const { workflow } = get();
    if (!workflow) return;

    const prepared = prepareAgentActionUpdate(workflow, agentName);
    if (!prepared.ok) {
      notifyAgentToolError(prepared.error);
      return;
    }
    const agentAction = prepared.location.action;

    // Create updated agent with new tool
    const updatedAgent = {
      ...agentAction,
      tools: {
        ...(agentAction.tools ?? {}),
        [toolName]: tool,
      },
    };

    prepared.location.container[prepared.actionName] = updatedAgent;

    set({
      workflow: prepared.workflow,
      workflowJson: JSON.stringify(prepared.workflow, null, 2),
      isDirty: true, changeCount: get().changeCount + 1,
    });
  },

  /**
   * Delete a tool from an agent
   */
  deleteToolFromAgent: (agentName: string, toolName: string) => {
    const { workflow } = get();
    if (!workflow) return;

    const prepared = prepareAgentActionUpdate(workflow, agentName);
    if (!prepared.ok) {
      notifyAgentToolError(prepared.error);
      return;
    }
    const agentAction = prepared.location.action;

    // Check if tool exists
    if (!agentAction.tools?.[toolName]) return;

    // Create new tools object without the deleted tool
    const { [toolName]: _removed, ...remainingTools } = agentAction.tools;

    // Create updated agent with tool removed
    const updatedAgent = {
      ...agentAction,
      tools: remainingTools,
    };

    prepared.location.container[prepared.actionName] = updatedAgent;

    // Drop the tool's stored layout position.
    const deleteToolIdentity = get().currentWorkflowIdentity;
    if (deleteToolIdentity) {
      useWorkflowLayoutStore.getState().removeTool(deleteToolIdentity, get().currentMode, prepared.actionName, toolName);
    }

    set({
      workflow: prepared.workflow,
      workflowJson: JSON.stringify(prepared.workflow, null, 2),
      isDirty: true, changeCount: get().changeCount + 1,
    });
  },

  /**
   * Update tool metadata (description, agentParameterSchema, etc.)
   */
  updateTool: (agentName: string, toolName: string, toolUpdate: Partial<AgentTool>) => {
    const { workflow } = get();
    if (!workflow) return;

    const prepared = prepareAgentActionUpdate(workflow, agentName);
    if (!prepared.ok) {
      notifyAgentToolError(prepared.error);
      return;
    }
    const agentAction = prepared.location.action;

    const existingTool = agentAction.tools?.[toolName];
    if (!existingTool) return;

    // Merge tool updates with existing tool. Only carry `actions` if it
    // exists on the update or the existing tool — never default to `{}`,
    // which would pollute MCP tools (which have no actions branch).
    const mergedActions = toolUpdate.actions ?? existingTool.actions;
    const updatedTool: AgentTool = {
      ...existingTool,
      ...toolUpdate,
      ...(mergedActions !== undefined ? { actions: mergedActions } : {}),
    };

    // Create updated agent with modified tool
    const updatedAgent = {
      ...agentAction,
      tools: {
        ...(agentAction.tools ?? {}),
        [toolName]: updatedTool,
      },
    };

    prepared.location.container[prepared.actionName] = updatedAgent;

    set({
      workflow: prepared.workflow,
      workflowJson: JSON.stringify(prepared.workflow, null, 2),
      isDirty: true, changeCount: get().changeCount + 1,
    });
  },

  /**
   * Update the MCP tool's allowedTools list under inputs.parameters.
   * Pass `undefined` to remove the allowedTools key (= "all tools allowed").
   * Deep-merge that preserves all other inputs/parameters fields.
   */
  setToolMcpAllowedTools: (agentName: string, toolName: string, allowedTools: string[] | undefined) => {
    const { workflow } = get();
    if (!workflow) return;

    const prepared = prepareAgentActionUpdate(workflow, agentName);
    if (!prepared.ok) {
      notifyAgentToolError(prepared.error);
      return;
    }
    const agentAction = prepared.location.action;

    const existingTool = agentAction.tools?.[toolName];
    if (!existingTool) return;

    const existingInputs = (existingTool.inputs ?? {}) as Record<string, unknown>;
    const existingParams = (existingInputs.parameters ?? {}) as Record<string, unknown>;

    const nextParams: Record<string, unknown> = { ...existingParams };
    if (allowedTools === undefined) {
      delete nextParams.allowedTools;
    } else {
      nextParams.allowedTools = allowedTools;
    }

    const updatedTool: AgentTool = {
      ...existingTool,
      inputs: {
        ...existingInputs,
        parameters: nextParams,
      },
      ...(existingTool.actions !== undefined ? { actions: existingTool.actions } : {}),
    };

    const updatedAgent = {
      ...agentAction,
      tools: {
        ...(agentAction.tools ?? {}),
        [toolName]: updatedTool,
      },
    };

    prepared.location.container[prepared.actionName] = updatedAgent;

    set({
      workflow: prepared.workflow,
      workflowJson: JSON.stringify(prepared.workflow, null, 2),
      isDirty: true,
      changeCount: get().changeCount + 1,
    });
  },

  /**
   * Write the connection reference key onto a managed MCP tool's
   * inputs.connectionReference.connectionName. Mirrors setToolMcpAllowedTools
   * but deep-merges inputs.connectionReference (preserving the existing
   * connectionReference.api block that carries the connector id).
   */
  setToolMcpConnectionName: (agentName: string, toolName: string, connectionName: string) => {
    const { workflow } = get();
    if (!workflow) return;

    const prepared = prepareAgentActionUpdate(workflow, agentName);
    if (!prepared.ok) {
      notifyAgentToolError(prepared.error);
      return;
    }
    const agentAction = prepared.location.action;

    const existingTool = agentAction.tools?.[toolName];
    if (!existingTool) return;

    const existingInputs = (existingTool.inputs ?? {}) as Record<string, unknown>;
    const existingConnectionReference = (existingInputs.connectionReference ?? {}) as Record<string, unknown>;

    const updatedTool: AgentTool = {
      ...existingTool,
      inputs: {
        ...existingInputs,
        connectionReference: {
          ...existingConnectionReference,
          connectionName,
        },
      },
      ...(existingTool.actions !== undefined ? { actions: existingTool.actions } : {}),
    };

    const updatedAgent = {
      ...agentAction,
      tools: {
        ...(agentAction.tools ?? {}),
        [toolName]: updatedTool,
      },
    };

    prepared.location.container[prepared.actionName] = updatedAgent;

    set({
      workflow: prepared.workflow,
      workflowJson: JSON.stringify(prepared.workflow, null, 2),
      isDirty: true,
      changeCount: get().changeCount + 1,
    });
  },

  /**
   * Update a specific inner action within a tool
   */
  updateToolInnerAction: (agentName: string, toolName: string, innerActionName: string, action: Action) => {
    const { workflow } = get();
    if (!workflow) return;

    const prepared = prepareAgentActionUpdate(workflow, agentName);
    if (!prepared.ok) {
      notifyAgentToolError(prepared.error);
      return;
    }
    const agentAction = prepared.location.action;

    const existingTool = agentAction.tools?.[toolName];
    if (!existingTool) return;

    // Update the inner action within the tool
    const updatedTool: AgentTool = {
      ...existingTool,
      actions: {
        ...(existingTool.actions ?? {}),
        [innerActionName]: action,
      },
    };

    // Create updated agent with modified tool
    const updatedAgent = {
      ...agentAction,
      tools: {
        ...(agentAction.tools ?? {}),
        [toolName]: updatedTool,
      },
    };

    prepared.location.container[prepared.actionName] = updatedAgent;

    set({
      workflow: prepared.workflow,
      workflowJson: JSON.stringify(prepared.workflow, null, 2),
      isDirty: true, changeCount: get().changeCount + 1,
    });
  },

  /**
   * Select a node in the workflow (single-select, clears multi-selection)
   */
  selectNode: (node) => {
    set({ 
      selectedNode: node,
      selectedNodes: node ? [node] : [],
    });
  },

  /**
   * Open the node editor for a node without changing multi-selection state
   * Used for normal clicks that should open the editor but preserve selection
   */
  openNodeEditor: (node) => {
    set({ selectedNode: node });
  },

  /**
   * Close the node editor without changing multi-selection state
   * Used when closing the parameter panel to preserve multi-selection
   */
  closeNodeEditor: () => {
    set({ selectedNode: null });
  },

  /**
   * Highlight a single node for keyboard navigation without opening
   * the node editor. Tab / arrow keys use this so users can roam the
   * graph; Enter / Space then promotes the focused node into the
   * editor via `openNodeEditor`.
   */
  focusNode: (node) => {
    set({
      selectedNodes: node ? [node] : [],
      selectedNode: null,
    });
  },

  /**
   * Select a node by name and type (single-select, clears multi-selection)
   */
  selectNodeByName: (name: string, type: 'trigger' | 'action', connectionName?: string) => {
    const id = type === 'trigger' ? `trigger-${name}` : `action-${name}`;

    // Detect ForEach scope context for actions (cached to avoid repeated tree walks)
    let scopeContext: ScopeContext | undefined;
    if (type === 'action') {
      const workflow = get().workflow;
      if (workflow?.definition?.actions) {
        const workflowKey = JSON.stringify(Object.keys(workflow.definition.actions));
        if (workflowKey !== scopeContextCacheKey) {
          scopeContextCache.clear();
          scopeContextCacheKey = workflowKey;
        }
        if (!scopeContextCache.has(name)) {
          scopeContextCache.set(name, findScopeContext(name, workflow.definition.actions));
        }
        scopeContext = scopeContextCache.get(name);
      }
    }

    const node: SelectedNode = {
      id, name, type,
      ...(connectionName ? { connectionName } : {}),
      ...(scopeContext ? { scopeContext } : {}),
    };
    set({
      selectedNode: node,
      selectedNodes: [node],
    });
  },

  /**
   * Select a tool by agent name and tool name.
   *
   * The canvas tool-node id (and the template-mock map key) uses the *inner
   * action name* — `tool-{agentName}-{innerActionName}` — so the click /
   * select flow has to resolve that inner action here, not blindly stamp the
   * outer tool name. Without this, a tool whose `actions[]` map key differs
   * from the tool name (any agent template with a real connector inside the
   * tool) selects under a never-rendered id and the Mock Data tab / mocked
   * badge can't find their entry.
   */
  selectTool: (agentName: string, toolName: string) => {
    const workflow = get().workflow;
    const actionName = agentName.includes('/') ? agentName.split('/').pop()! : agentName;
    const locations = workflow
      ? findActionLocations(workflow.definition.actions, actionName)
      : [];

    if (locations.length > 1) {
      notifyAgentToolError(
        `Agent "${agentName}" appears more than once in the workflow. Rename the duplicate actions before managing tools.`,
      );
      return;
    }

    const action = locations[0]?.action;
    const scopedAgentName = locations[0]?.scopedName ?? agentName;
    if (action && !isAgentAction(action)) {
      notifyAgentToolError(`Action "${agentName}" is not an Agent action`);
      return;
    }

    // Preserve the historical fallback selection when workflow data is still
    // loading or the requested tool is absent.
    const tool = action?.tools?.[toolName];
    const innerActionName = tool?.actions ? Object.keys(tool.actions)[0] : undefined;
    const id = `tool-${actionName}-${innerActionName ?? toolName}`;
    const node: SelectedNode = {
      id,
      name: toolName,
      type: 'tool' as const,
      parentAgentName: scopedAgentName,
      toolName,
    };
    set({
      selectedNode: node,
      selectedNodes: [node],
    });
  },

  /**
   * Clear the current selection (single and multi)
   */
  clearSelection: () => {
    set({ selectedNode: null, selectedNodes: [] });
  },

  /**
   * Select multiple nodes at once (replaces current selection).
   *
   * Only mirrors into the singular `selectedNode` (which drives the
   * node editor) when the call resolves to a single node — otherwise
   * a Ctrl/Cmd+A "select all" would silently open the editor on the
   * first node (typically the trigger).
   */
  selectNodes: (nodes: SelectedNode[]) => {
    set({
      selectedNodes: nodes,
      selectedNode: nodes.length === 1 ? nodes[0]! : null,
    });
  },

  /**
   * Add multiple nodes to the current selection (for shift+box selection)
   * Does NOT open the node editor - only manages selection state
   */
  addNodesToSelection: (nodes: SelectedNode[]) => {
    const { selectedNodes } = get();
    // Filter out duplicates
    const newNodes = nodes.filter(n => !selectedNodes.some(existing => existing.id === n.id));
    if (newNodes.length === 0) {
      return; // Nothing new to add
    }
    set({
      selectedNodes: [...selectedNodes, ...newNodes],
      selectedNode: null, // Don't open editor during multi-select
    });
  },

  /**
   * Add a node to the current multi-selection
   */
  addToSelection: (node: SelectedNode) => {
    const { selectedNodes } = get();
    // Don't add duplicates
    if (selectedNodes.some(n => n.id === node.id)) {
      return;
    }
    const newNodes = [...selectedNodes, node];
    const firstNode = newNodes[0]!;
    set({
      selectedNodes: newNodes,
      selectedNode: firstNode,
    });
  },

  /**
   * Remove a node from the current multi-selection
   */
  removeFromSelection: (nodeId: string) => {
    const { selectedNodes } = get();
    const newNodes = selectedNodes.filter(n => n.id !== nodeId);
    const firstNode = newNodes.length > 0 ? newNodes[0]! : null;
    set({
      selectedNodes: newNodes,
      selectedNode: firstNode,
    });
  },

  /**
   * Toggle a node's selection state (for Shift+click multi-selection)
   * Does NOT open the node editor - only manages selection state
   */
  toggleNodeSelection: (node: SelectedNode) => {
    const { selectedNodes } = get();
    const isSelected = selectedNodes.some(n => n.id === node.id);
    if (isSelected) {
      // Remove from selection
      const newNodes = selectedNodes.filter(n => n.id !== node.id);
      // Only update selectedNode if we're removing the last node
      // Otherwise keep selectedNode null to prevent editor from opening
      set({
        selectedNodes: newNodes,
        selectedNode: null, // Don't open editor during multi-select
      });
    } else {
      // Add to selection
      const newNodes = [...selectedNodes, node];
      set({
        selectedNodes: newNodes,
        selectedNode: null, // Don't open editor during multi-select
      });
    }
  },

  /**
   * Check if a node is currently selected
   */
  isNodeSelected: (nodeId: string) => {
    const { selectedNodes } = get();
    return selectedNodes.some(n => n.id === nodeId);
  },

  /**
   * Set the layouting state
   */
  setIsLayouting: (isLayouting: boolean) => {
    if (get().isLayouting === isLayouting) return;
    set({ isLayouting });
  },

  /**
   * Apply layout positions to all nodes in the workflow.
   *
   * Positions are stored in the client-side `workflowLayoutStore` keyed by
   * the React Flow node id (`trigger-{name}` / `action-{scopedPath}`). The
   * incoming map already uses those ids, so this is a keyed merge. Keeping
   * positions that are outside a filtered layout request prevents collapsed
   * descendants from losing geometry.
   * `skipDirty` is kept as a vestigial parameter for callers that still
   * pass it; the layout store is independent of workflow dirty state, so
   * it now has no effect.
   */
applyLayoutPositions: (positions: Map<string, Position>, skipDirty?: boolean) => {
    const identity = get().currentWorkflowIdentity;
    if (!identity) return;
    useWorkflowLayoutStore.getState().applyNodePositions(identity, get().currentMode, positions);
    // Formatting/layout changes affect the sidecar (saved state) even when the workflow definition is unchanged.
    if (!skipDirty) {
      set((state) => ({ isDirty: true, changeCount: state.changeCount + 1 }));
    }
  },

  migrateLegacyPositions: (identity) => {
    const { workflow } = get();
    if (!workflow) return;

    const triggerPositions = extractTriggerPositions(workflow.definition.triggers ?? {});
    const actionPositions = extractActionPositions(workflow.definition.actions ?? {});
    const toolNodePositions = extractToolNodePositions(workflow.definition.actions ?? {});
    const combined: Record<string, Position> = { ...triggerPositions, ...actionPositions };

    const nothingToMigrate =
      Object.keys(combined).length === 0 && Object.keys(toolNodePositions).length === 0;
    if (nothingToMigrate) return;

    useWorkflowLayoutStore.getState().importLayout(identity, get().currentMode, {
      nodePositions: combined,
      toolNodePositions,
    });

    const stripped: WorkflowDefinition = {
      ...workflow,
      definition: {
        ...workflow.definition,
        triggers: stripTriggerPositions(workflow.definition.triggers ?? {}),
        actions: stripActionPositions(workflow.definition.actions ?? {}),
      },
    };

    // Don't mark dirty: positions moving from metadata to the layout store
    // is a representation change, not a user edit.
    set({
      workflow: stripped,
      workflowJson: JSON.stringify(stripped, null, 2),
    });
  },

  // Dirty state actions
  markDirty: () => {
    set((state) => ({ isDirty: true, changeCount: state.changeCount + 1 }));
  },

  markClean: () => {
    // The Code panel buffer's dirty flag is owned by `CodeEditor.handleChange`
    // — clearing it here would race with in-flight keystrokes and drop edits
    // typed during the save. If the buffer happens to be in sync with the
    // tree, its next debounced tick will already have cleared the flag.
    set({ isDirty: false });
  },

  setCodeBufferState: ({ dirty, parseError }) => {
    // Bail out on no-op writes so subscribers of `useIsDraftDirty` aren't
    // woken up on every keystroke that didn't change either signal.
    const { codeBufferDirty, codeBufferParseError } = get();
    if (codeBufferDirty === dirty && codeBufferParseError === parseError) return;
    set({ codeBufferDirty: dirty, codeBufferParseError: parseError });
  },

  // Change count action
  resetChangeCount: () => {
    set({ changeCount: 0 });
  },

  // Note actions
  addNote: (position) => {
    const id = crypto.randomUUID();
    set((state) => {
      if (!state.workflow) return state;
      const notes = { ...(state.workflow.notes ?? {}) };
      notes[id] = {
        content: '',
        color: DEFAULT_NOTE_COLOR,
        metadata: {
          position,
          width: DEFAULT_NOTE_SIZE.width,
          height: DEFAULT_NOTE_SIZE.height,
        },
      };
      const workflow = { ...state.workflow, notes };
      return {
        workflow,
        workflowJson: JSON.stringify(workflow, null, 2),
        isDirty: true,
        changeCount: state.changeCount + 1,
      };
    });
    return id;
  },

  updateNote: (id, noteUpdate) => {
    set((state) => {
      if (!state.workflow?.notes?.[id]) return state;
      const existing = state.workflow.notes[id];
      const updated: Note = {
        content: noteUpdate.content ?? existing.content,
        color: noteUpdate.color ?? existing.color,
        metadata: {
          position: noteUpdate.metadata?.position ?? existing.metadata.position,
          width: noteUpdate.metadata?.width ?? existing.metadata.width,
          height: noteUpdate.metadata?.height ?? existing.metadata.height,
        },
      };
      const notes = { ...state.workflow.notes, [id]: updated };
      const workflow = { ...state.workflow, notes };
      return {
        workflow,
        workflowJson: JSON.stringify(workflow, null, 2),
        isDirty: true,
        changeCount: state.changeCount + 1,
      };
    });
  },

  deleteNote: (id) => {
    set((state) => {
      if (!state.workflow?.notes?.[id]) return state;
      const notes = { ...state.workflow.notes };
      delete notes[id];
      const workflow = { ...state.workflow, notes };
      return {
        workflow,
        workflowJson: JSON.stringify(workflow, null, 2),
        isDirty: true,
        changeCount: state.changeCount + 1,
      };
    });
  },

  // ──────────────────────────────────────────────────────────────────────
  // Mock data — sidecar state, intentionally NOT written into `workflowJson`
  // or guarded by `isDirty` (mock data is not part of the saved definition).
  // ──────────────────────────────────────────────────────────────────────

  setMockedNode: (nodeId, info) => {
    set((state) => ({
      mockedNodes: { ...state.mockedNodes, [nodeId]: info },
      isDirty: true,
      changeCount: state.changeCount + 1,
    }));
  },

  removeMockedNode: (nodeId) => {
    set((state) => {
      if (!(nodeId in state.mockedNodes)) return state;
      const { [nodeId]: _removed, ...rest } = state.mockedNodes;
      return { mockedNodes: rest, isDirty: true, changeCount: state.changeCount + 1 };
    });
  },

  // Bulk replace (template injection): non-dirtying. Template apply already
  // dirties the workflow via its action edits, so the injected mocks ride the
  // resulting save without needing a redundant dirty bump here.
  setMockedNodes: (mocks) => {
    set({ mockedNodes: mocks });
  },

  setMockNodeEnabled: (nodeId, enabled) => {
    set((state) => {
      const entry = state.mockedNodes[nodeId];
      // Trigger entries have no enabled toggle — their payload is always sent
      // with a mock run. The typeof guard also covers malformed sidecar
      // entries (parseMockData doesn't validate per-entry shapes, and `in`
      // throws on primitives).
      if (!entry || typeof entry !== 'object' || !('actionName' in entry)) return state;
      return {
        mockedNodes: {
          ...state.mockedNodes,
          [nodeId]: { ...entry, enabled },
        },
        isDirty: true,
        changeCount: state.changeCount + 1,
      };
    });
  },

  setMockRunEnabled: (enabled) => {
    set((state) => ({
      mockRunEnabled: enabled,
      isDirty: true,
      changeCount: state.changeCount + 1,
    }));
  },

  // Hydration from the persisted sidecar — must NOT mark dirty, or load and
  // mode-switch reapply would immediately trigger an autosave.
  hydrateMocks: (mockedNodes, mockRunEnabled) => {
    set({ mockedNodes, mockRunEnabled });
  },

  clearMocks: () => {
    set({ mockedNodes: {}, mockRunEnabled: undefined });
  },
}));
